import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import {
  Archive,
  AlertTriangle,
  ArrowLeft,
  Bot,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Circle,
  Download,
  Edit3,
  FileText,
  Lightbulb,
  Loader2,
  Maximize2,
  MessageSquare,
  Minimize2,
  RefreshCw,
  Search,
  Send,
  ShieldCheck,
  UploadCloud,
  User,
  X,
} from 'lucide-react';
import toast from 'react-hot-toast';
import MarkdownChatContent from '../components/MarkdownChatContent';
import {
  NDA_INFORMATION_FLOW_OPTIONS,
  NdaWorkspace,
  buildNdaEscalationActions,
  buildNdaLegalReviewReasons,
  formatNdaJobStatus,
  formatNdaPages,
  formatNdaSeverity,
  ndaDispositionDisplay,
  ndaRuleSeverity,
  ndaProgress,
  describeNdaFillInLocation,
  ndaScopeDisplay,
  normalizeNdaReview,
  downloadNdaReviewCopy,
} from './ContractQAAgent';
import { formatNdaClauseLines, isNdaEnumeratedLine } from './ndaClauseText';

const CONTRACT_QA_API_BASE = (process.env.REACT_APP_CONTRACT_QA_API_BASE || '/api/contract-qa').replace(/\/$/, '');
// The Sylvan parties named in Legal's approved form; the review API accepts only these.
export const NDA_SYLVAN_ENTITIES = [
  'Sylvan Global Holdings, LP',
  'F M Sylvan, Inc.',
  'Andy J. Egan Co., LLC',
  'Pettus Plumbing & Piping, LLC',
  'FM Sylvan Canada Ltd.',
];
export const NDA_CANADA_ENTITY = 'FM Sylvan Canada Ltd.';
export const NDA_GOVERNING_LAWS = { michigan: 'Michigan', ontario: 'Ontario' };

/** Prefer the filename the server sent; fall back to the counterparty if it is absent. */
const ndaFindingsMemoName = (contentDisposition, counterparty) => {
  const match = /filename="?([^"';]+)"?/i.exec(contentDisposition || '');
  if (match) return match[1];
  const stem = String(counterparty || '').replace(/[^a-z0-9]+/gi, '_').replace(/^_+|_+$/g, '');
  return `${stem || 'Sylvan'}_NDA_Review_Findings.docx`;
};
const US_STATES = [
  ['AL', 'Alabama'], ['AK', 'Alaska'], ['AZ', 'Arizona'], ['AR', 'Arkansas'],
  ['CA', 'California'], ['CO', 'Colorado'], ['CT', 'Connecticut'], ['DE', 'Delaware'],
  ['FL', 'Florida'], ['GA', 'Georgia'], ['HI', 'Hawaii'], ['ID', 'Idaho'],
  ['IL', 'Illinois'], ['IN', 'Indiana'], ['IA', 'Iowa'], ['KS', 'Kansas'],
  ['KY', 'Kentucky'], ['LA', 'Louisiana'], ['ME', 'Maine'], ['MD', 'Maryland'],
  ['MA', 'Massachusetts'], ['MI', 'Michigan'], ['MN', 'Minnesota'], ['MS', 'Mississippi'],
  ['MO', 'Missouri'], ['MT', 'Montana'], ['NE', 'Nebraska'], ['NV', 'Nevada'],
  ['NH', 'New Hampshire'], ['NJ', 'New Jersey'], ['NM', 'New Mexico'], ['NY', 'New York'],
  ['NC', 'North Carolina'], ['ND', 'North Dakota'], ['OH', 'Ohio'], ['OK', 'Oklahoma'],
  ['OR', 'Oregon'], ['PA', 'Pennsylvania'], ['RI', 'Rhode Island'], ['SC', 'South Carolina'],
  ['SD', 'South Dakota'], ['TN', 'Tennessee'], ['TX', 'Texas'], ['UT', 'Utah'],
  ['VT', 'Vermont'], ['VA', 'Virginia'], ['WA', 'Washington'], ['WV', 'West Virginia'],
  ['WI', 'Wisconsin'], ['WY', 'Wyoming'],
];
const NDA_FOLLOW_UP_PROMPTS = [
  'Why did this NDA receive its disposition?',
  'Which changes are required before signing?',
  'Explain the information-flow assessment.',
  'What policy rules need Legal review?',
];

const NdaAgent = () => {
  const [homeTab, setHomeTab] = useState('review');
  const [file, setFile] = useState(null);
  const [informationFlow, setInformationFlow] = useState('');
  const [job, setJob] = useState(null);
  const [review, setReview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [assistantExpanded, setAssistantExpanded] = useState(false);
  const [compactPane, setCompactPane] = useState('results');
  const [redlineValues, setRedlineValues] = useState({});
  const [redlineJob, setRedlineJob] = useState(null);
  const [redlineArtifacts, setRedlineArtifacts] = useState([]);
  const [redlineLoading, setRedlineLoading] = useState(false);
  const [redlineDownloading, setRedlineDownloading] = useState(false);
  const [redlineError, setRedlineError] = useState('');
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const [wordExporting, setWordExporting] = useState(false);
  const [consistencyFiles, setConsistencyFiles] = useState([]);
  const [consistencyJob, setConsistencyJob] = useState(null);
  const [consistencyResult, setConsistencyResult] = useState(null);
  const [consistencyLoading, setConsistencyLoading] = useState(false);
  const [consistencyError, setConsistencyError] = useState('');
  const [draftForm, setDraftForm] = useState({
    counterpartyLegalName: '',
    counterpartyAddressLine1: '',
    counterpartyAddressLine2: '',
    counterpartyCity: '',
    counterpartyState: '',
    counterpartyPostalCode: '',
    businessPurpose: '',
    effectiveDate: '',
    termYears: 2,
    sylvanEntity: '',
    governingLaw: 'michigan',
  });
  const [draftJob, setDraftJob] = useState(null);
  const [draftArtifacts, setDraftArtifacts] = useState([]);
  const [draftLoading, setDraftLoading] = useState(false);
  const [draftDownloading, setDraftDownloading] = useState(false);
  const [draftError, setDraftError] = useState('');
  const [registerEntries, setRegisterEntries] = useState([]);
  const [registerPagination, setRegisterPagination] = useState({ page: 1, totalPages: 0, total: 0 });
  const [registerFilters, setRegisterFilters] = useState({ query: '', entryType: '', disposition: '' });
  const [registerLoading, setRegisterLoading] = useState(false);
  const [registerError, setRegisterError] = useState('');
  const [historicalDetail, setHistoricalDetail] = useState(null);
  const [registerDownload, setRegisterDownload] = useState('');
  const registerFiltersRef = useRef(registerFilters);
  const runTokenRef = useRef(0);
  const redlineTokenRef = useRef(0);
  const consistencyTokenRef = useRef(0);
  const redlineSectionRef = useRef(null);
  const exportMenuRef = useRef(null);
  const draftTokenRef = useRef(0);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages.length, chatLoading]);

  useEffect(() => {
    const wideLayout = window.matchMedia?.('(min-width: 1400px)');
    if (!wideLayout) return undefined;
    const handleLayoutChange = (event) => {
      if (!event.matches) setAssistantExpanded(false);
    };
    handleLayoutChange(wideLayout);
    if (wideLayout.addEventListener) {
      wideLayout.addEventListener('change', handleLayoutChange);
      return () => wideLayout.removeEventListener('change', handleLayoutChange);
    }
    wideLayout.addListener?.(handleLayoutChange);
    return () => wideLayout.removeListener?.(handleLayoutChange);
  }, []);

  useEffect(() => {
    if (!exportMenuOpen) return undefined;
    const handlePointerDown = (event) => {
      if (!exportMenuRef.current?.contains(event.target)) setExportMenuOpen(false);
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setExportMenuOpen(false);
    };
    document.addEventListener('mousedown', handlePointerDown);
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [exportMenuOpen]);

  const resetReview = () => {
    runTokenRef.current += 1;
    redlineTokenRef.current += 1;
    setFile(null);
    setInformationFlow('');
    setJob(null);
    setReview(null);
    setLoading(false);
    setMessages([]);
    setInput('');
    setChatLoading(false);
    setAssistantExpanded(false);
    setCompactPane('results');
    setRedlineValues({});
    setRedlineJob(null);
    setRedlineArtifacts([]);
    setRedlineLoading(false);
    setRedlineDownloading(false);
    setRedlineError('');
    consistencyTokenRef.current += 1;
    setConsistencyFiles([]);
    setConsistencyJob(null);
    setConsistencyResult(null);
    setConsistencyLoading(false);
    setConsistencyError('');
    setHistoricalDetail(null);
  };

  const resetDraft = () => {
    draftTokenRef.current += 1;
    setDraftForm({
      counterpartyLegalName: '',
      counterpartyAddressLine1: '',
      counterpartyAddressLine2: '',
      counterpartyCity: '',
      counterpartyState: '',
      counterpartyPostalCode: '',
      businessPurpose: '',
      effectiveDate: '',
      termYears: 2,
      sylvanEntity: '',
      governingLaw: 'michigan',
    });
    setDraftJob(null);
    setDraftArtifacts([]);
    setDraftLoading(false);
    setDraftDownloading(false);
    setDraftError('');
    setHistoricalDetail(null);
  };

  useEffect(() => {
    registerFiltersRef.current = registerFilters;
  }, [registerFilters]);

  const loadRegister = useCallback(async (page = 1, filters = registerFiltersRef.current) => {
    setRegisterLoading(true);
    setRegisterError('');
    try {
      const params = new URLSearchParams({ page: String(page), page_size: '20' });
      if (filters.query.trim()) params.set('query', filters.query.trim());
      if (filters.entryType) params.set('entry_type', filters.entryType);
      if (filters.disposition) params.set('disposition', filters.disposition);
      const response = await fetch(`${CONTRACT_QA_API_BASE}/nda/register?${params}`, { credentials: 'include' });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.detail || 'Could not load the NDA Register.');
      setRegisterEntries(data.entries || []);
      setRegisterPagination(data.pagination || { page, totalPages: 0, total: 0 });
    } catch (error) {
      setRegisterEntries([]);
      setRegisterError(error.message || 'Could not load the NDA Register.');
    } finally {
      setRegisterLoading(false);
    }
  }, []);

  useEffect(() => {
    if (homeTab === 'register' && !historicalDetail) loadRegister(1);
  }, [homeTab, historicalDetail, loadRegister]);

  const openRegisterEntry = async (entry) => {
    setRegisterLoading(true);
    setRegisterError('');
    try {
      const response = await fetch(`${CONTRACT_QA_API_BASE}/nda/register/${encodeURIComponent(entry.entryId)}`, {
        credentials: 'include',
      });
      const detail = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(detail.detail || 'Could not open this NDA record.');
      setHistoricalDetail(detail);
      if (detail.entryType === 'review') {
        const reviewResponse = await fetch(`${CONTRACT_QA_API_BASE}/nda/jobs/${detail.jobId}/review`, {
          credentials: 'include',
        });
        const reviewData = await reviewResponse.json().catch(() => ({}));
        if (!reviewResponse.ok) throw new Error(reviewData.detail || 'Could not load this NDA review.');
        const normalized = normalizeNdaReview(reviewData);
        setFile({ name: detail.sourceFilename || 'NDA document' });
        setInformationFlow(detail.informationFlow || 'mutual');
        setJob({
          jobId: detail.jobId,
          status: 'succeeded',
          stage: 'completed',
          disposition: detail.disposition,
          sourceFilename: detail.sourceFilename,
          ...(detail.processing || {}),
        });
        setReview(normalized);
        setRedlineValues(initialRedlineValues(normalized));
        setMessages([{
          role: 'assistant',
          content: buildNdaAssistantBlurb(normalized, detail.sourceFilename),
          citedRuleIds: [],
        }]);
        setCompactPane('results');
        setHomeTab('review');
      } else {
        const storedDraft = detail.draft || {};
        setDraftForm({
          counterpartyLegalName: storedDraft.counterpartyLegalName || detail.counterparty || '',
          counterpartyAddressLine1: storedDraft.counterpartyAddressLine1 || '',
          counterpartyAddressLine2: storedDraft.counterpartyAddressLine2 || '',
          counterpartyCity: storedDraft.counterpartyCity || '',
          counterpartyState: storedDraft.counterpartyState || '',
          counterpartyPostalCode: storedDraft.counterpartyPostalCode || '',
          businessPurpose: storedDraft.businessPurpose || detail.businessPurpose || '',
          effectiveDate: storedDraft.effectiveDate || detail.effectiveDate || '',
          termYears: storedDraft.termYears || Number.parseInt(detail.confidentialityTerm, 10) || 2,
          sylvanEntity: storedDraft.sylvanEntity || '',
          governingLaw: storedDraft.governingLaw || 'michigan',
        });
        setDraftJob(storedDraft);
        setDraftArtifacts(detail.artifacts || []);
        setHomeTab('draft');
      }
    } catch (error) {
      setHistoricalDetail(null);
      setRegisterError(error.message || 'Could not open this NDA record.');
    } finally {
      setRegisterLoading(false);
    }
  };

  const backToRegister = () => {
    resetReview();
    resetDraft();
    setHomeTab('register');
  };

  // Deep link: /nda-agent?review=<jobId> opens a completed review directly — the Legal
  // email points here, so a recipient lands on the findings instead of a bare id.
  const openReviewByJobId = async (jobId) => {
    setHomeTab('register');
    setRegisterLoading(true);
    setRegisterError('');
    try {
      const jobResponse = await fetch(`${CONTRACT_QA_API_BASE}/nda/jobs/${encodeURIComponent(jobId)}`, {
        credentials: 'include',
      });
      const jobData = await jobResponse.json().catch(() => ({}));
      if (!jobResponse.ok) throw new Error(jobData.detail || 'Could not open this NDA review.');
      if (jobData.status !== 'succeeded') throw new Error('This NDA review has not completed.');
      const reviewResponse = await fetch(`${CONTRACT_QA_API_BASE}/nda/jobs/${encodeURIComponent(jobId)}/review`, {
        credentials: 'include',
      });
      const reviewData = await reviewResponse.json().catch(() => ({}));
      if (!reviewResponse.ok) throw new Error(reviewData.detail || 'Could not load this NDA review.');
      const normalized = normalizeNdaReview(reviewData);
      setHistoricalDetail({
        entryType: 'review',
        jobId,
        sourceFilename: jobData.sourceFilename,
        disposition: jobData.disposition,
        counterparty: jobData.counterparty
          || normalized.documentAssessment?.derivedCounterparty
          || jobData.sourceFilename,
        informationFlow: jobData.expectedInformationFlow,
        sourceCompletedAt: jobData.completedAt,
      });
      setFile({ name: jobData.sourceFilename || 'NDA document' });
      setInformationFlow(jobData.expectedInformationFlow || 'mutual');
      setJob({
        jobId,
        status: 'succeeded',
        stage: 'completed',
        disposition: jobData.disposition,
        sourceFilename: jobData.sourceFilename,
      });
      setReview(normalized);
      setRedlineValues(initialRedlineValues(normalized));
      setMessages([{
        role: 'assistant',
        content: buildNdaAssistantBlurb(normalized, jobData.sourceFilename),
        citedRuleIds: [],
      }]);
      setCompactPane('results');
      setHomeTab('review');
    } catch (error) {
      setHistoricalDetail(null);
      setRegisterError(error.message || 'Could not open this NDA review.');
    } finally {
      setRegisterLoading(false);
    }
  };

  useEffect(() => {
    const linkedJobId = new URLSearchParams(window.location.search).get('review');
    if (linkedJobId) openReviewByJobId(linkedJobId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const downloadRegisterArtifact = async (url, name) => {
    if (!url || registerDownload) return;
    setRegisterDownload(name);
    try {
      const response = await fetch(`${CONTRACT_QA_API_BASE}${url.replace('/v1/nda', '/nda')}`, {
        credentials: 'include',
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.detail || 'This NDA file is unavailable.');
      }
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = name;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    } catch (error) {
      toast.error(error.message || 'This NDA file is unavailable.');
    } finally {
      setRegisterDownload('');
    }
  };

  const startReview = async () => {
    if (!file) {
      toast.error('Upload an NDA to review.');
      return;
    }
    if (!informationFlow) {
      toast.error('Select the expected information flow.');
      return;
    }

    const runToken = runTokenRef.current + 1;
    runTokenRef.current = runToken;
    setLoading(true);
    setReview(null);
    setJob({
      status: 'queued',
      stage: 'queued',
      progressMessage: 'Review queued.',
    });

    try {
      const formData = new FormData();
      formData.append('expected_information_flow', informationFlow);
      formData.append('document', file);
      const response = await fetch(`${CONTRACT_QA_API_BASE}/nda/jobs`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.detail || 'NDA review upload failed.');
      if (runTokenRef.current !== runToken) return;
      setJob(data);
      await pollNdaJob(data.jobId, runToken);
    } catch (error) {
      if (runTokenRef.current !== runToken) return;
      console.error('NDA review failed:', error);
      const message = userSafeNdaFailureMessage(error.message);
      setJob((current) => ({
        ...(current || {}),
        status: 'failed',
        error: message,
      }));
      toast.error(message);
    } finally {
      if (runTokenRef.current === runToken) setLoading(false);
    }
  };

  const pollNdaJob = async (jobId, runToken) => {
    for (let attempt = 0; attempt < 240; attempt += 1) {
      await delay(attempt === 0 ? 1000 : 5000);
      if (runTokenRef.current !== runToken) return;
      const statusResponse = await fetch(`${CONTRACT_QA_API_BASE}/nda/jobs/${jobId}`, {
        credentials: 'include',
      });
      const statusData = await statusResponse.json().catch(() => ({}));
      if (runTokenRef.current !== runToken) return;
      if (!statusResponse.ok) throw new Error(statusData.detail || 'Could not load NDA review status.');
      setJob(statusData);
      if (statusData.status === 'succeeded') {
        const reviewResponse = await fetch(`${CONTRACT_QA_API_BASE}/nda/jobs/${jobId}/review`, {
          credentials: 'include',
        });
        const reviewData = await reviewResponse.json().catch(() => ({}));
        if (runTokenRef.current !== runToken) return;
        if (!reviewResponse.ok) throw new Error(reviewData.detail || 'Could not load NDA review results.');
        const normalized = normalizeNdaReview(reviewData);
        setReview(normalized);
        setRedlineValues(initialRedlineValues(normalized));
        setRedlineJob(null);
        setRedlineArtifacts([]);
        setRedlineError('');
        setCompactPane('results');
        setMessages([{
          id: `nda-ready-${Date.now()}`,
          role: 'assistant',
          text: buildNdaAssistantBlurb(normalized, file?.name),
        }]);
        // Supporting documents chosen at intake run on their own once the review exists —
        // the API attaches a consistency check to a completed NDA job, so it cannot start
        // any earlier. Deliberately not awaited: the review results should render now, and
        // runConsistencyCheck reports its own failures without touching this one.
        if (consistencyFiles.length) runConsistencyCheck(jobId);
        return;
      }
      if (statusData.status === 'failed') {
        throw new Error(userSafeNdaFailureMessage(statusData.error, statusData.errorCode));
      }
    }
    throw new Error('NDA review timed out while waiting for the API.');
  };

  const generateDraft = async () => {
    const values = Object.fromEntries(
      Object.entries(draftForm).map(([key, value]) => [key, typeof value === 'string' ? value.trim() : value])
    );
    if (
      !values.counterpartyLegalName
      || !values.counterpartyAddressLine1
      || !values.counterpartyCity
      || !values.counterpartyState
      || !values.counterpartyPostalCode
      || !values.businessPurpose
      || !values.effectiveDate
      || !values.sylvanEntity
    ) {
      toast.error('Complete all required draft fields.');
      return;
    }
    const termYears = Number(values.termYears);
    if (!Number.isInteger(termYears) || termYears < 1 || termYears > 10) {
      toast.error('Agreement term must be a whole number from 1 to 10 years.');
      return;
    }
    const token = draftTokenRef.current + 1;
    draftTokenRef.current = token;
    setDraftLoading(true);
    setDraftError('');
    setDraftArtifacts([]);
    setDraftJob({ status: 'queued', stage: 'queued', progressMessage: 'Preparing the approved Sylvan NDA.' });
    try {
      const response = await fetch(`${CONTRACT_QA_API_BASE}/nda/drafts`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...values, termYears }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.detail || data.error || 'Sylvan NDA draft generation could not be started.');
      if (draftTokenRef.current !== token) return;
      setDraftJob(data);
      await pollNdaDraft(data.draftId, token);
    } catch (error) {
      if (draftTokenRef.current !== token) return;
      const message = userSafeDraftFailureMessage(error.message);
      setDraftError(message);
      setDraftJob((current) => ({ ...(current || {}), status: 'failed', error: message }));
      toast.error(message);
    } finally {
      if (draftTokenRef.current === token) setDraftLoading(false);
    }
  };

  const pollNdaDraft = async (draftId, token) => {
    for (let attempt = 0; attempt < 120; attempt += 1) {
      await delay(attempt === 0 ? 750 : 2000);
      if (draftTokenRef.current !== token) return;
      const response = await fetch(`${CONTRACT_QA_API_BASE}/nda/drafts/${draftId}`, { credentials: 'include' });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.detail || data.error || 'Could not load NDA draft status.');
      if (draftTokenRef.current !== token) return;
      setDraftJob(data);
      if (data.status === 'succeeded') {
        const artifactsResponse = await fetch(
          `${CONTRACT_QA_API_BASE}/nda/drafts/${draftId}/artifacts`,
          { credentials: 'include' }
        );
        const artifactsData = await artifactsResponse.json().catch(() => ({}));
        if (!artifactsResponse.ok) throw new Error(artifactsData.detail || 'Could not load the generated NDA draft.');
        setDraftArtifacts(artifactsData.artifacts || []);
        toast.success('Sylvan NDA draft is ready.');
        return;
      }
      if (data.status === 'failed') throw new Error(data.error || 'The Sylvan NDA draft could not be generated.');
    }
    throw new Error('NDA draft generation took longer than expected. Please try again.');
  };

  const downloadDraft = async () => {
    const artifact = draftArtifacts.find((item) => item.name === 'sylvan_nda_draft.docx');
    if (!artifact || !draftJob?.draftId || draftDownloading) return;
    setDraftDownloading(true);
    try {
      const response = await fetch(
        `${CONTRACT_QA_API_BASE}/nda/drafts/${draftJob.draftId}/artifacts/${encodeURIComponent(artifact.name)}`,
        { credentials: 'include' }
      );
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.detail || 'The generated NDA draft could not be downloaded.');
      }
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = `${draftForm.counterpartyLegalName.replace(/[^a-z0-9]+/gi, '_') || 'Sylvan'}_NDA.docx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    } catch (error) {
      toast.error(userSafeDraftFailureMessage(error.message));
    } finally {
      setDraftDownloading(false);
    }
  };

  const submitQuestion = async (messageText = input) => {
    const message = messageText.trim();
    if (!message || !review || !job?.jobId || chatLoading) return;
    setInput('');
    setChatLoading(true);
    setMessages((current) => [
      ...current,
      { id: `nda-user-${Date.now()}`, role: 'user', text: message },
    ]);
    try {
      const response = await fetch(`${CONTRACT_QA_API_BASE}/nda/jobs/${job.jobId}/chat`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.detail || 'NDA follow-up failed.');
      setMessages((current) => [
        ...current,
        {
          id: `nda-answer-${Date.now()}`,
          role: 'assistant',
          text: data.answer,
          citedRuleIds: data.cited_rule_ids || [],
          routedToLegal: Boolean(data.routed_to_legal),
        },
      ]);
    } catch (error) {
      console.error('NDA follow-up failed:', error);
      toast.error(error.message || 'NDA follow-up failed.');
      setMessages((current) => [
        ...current,
        {
          id: `nda-error-${Date.now()}`,
          role: 'assistant',
          kind: 'error',
          text: error.message || 'NDA follow-up failed.',
        },
      ]);
    } finally {
      setChatLoading(false);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      submitQuestion();
    }
  };

  // jobIdOverride lets the review's own completion start this before `job` state has
  // settled; the button path passes nothing and uses the job already on screen.
  const runConsistencyCheck = async (jobIdOverride) => {
    const targetJobId = typeof jobIdOverride === 'string' ? jobIdOverride : job?.jobId;
    if (!targetJobId || !consistencyFiles.length || consistencyLoading) return;
    const token = consistencyTokenRef.current + 1;
    consistencyTokenRef.current = token;
    setConsistencyLoading(true);
    setConsistencyError('');
    setConsistencyResult(null);
    setConsistencyJob({ status: 'queued', stage: 'preflight', progressMessage: 'Preparing the supporting-document consistency check.' });
    try {
      const formData = new FormData();
      consistencyFiles.forEach((item) => formData.append('documents', item));
      const response = await fetch(`${CONTRACT_QA_API_BASE}/nda/jobs/${targetJobId}/consistency`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.detail || 'The consistency check could not be started.');
      if (consistencyTokenRef.current !== token) return;
      setConsistencyJob(data);
      await pollConsistency(targetJobId, data.consistencyId, token);
    } catch (error) {
      if (consistencyTokenRef.current !== token) return;
      const message = error.message || 'The consistency check could not be completed.';
      setConsistencyError(message);
      setConsistencyJob((current) => ({ ...(current || {}), status: 'failed', error: message }));
      toast.error(message);
    } finally {
      if (consistencyTokenRef.current === token) setConsistencyLoading(false);
    }
  };

  const pollConsistency = async (jobId, consistencyId, token) => {
    for (let attempt = 0; attempt < 180; attempt += 1) {
      await delay(attempt === 0 ? 2000 : 5000);
      if (consistencyTokenRef.current !== token) return;
      const response = await fetch(
        `${CONTRACT_QA_API_BASE}/nda/jobs/${jobId}/consistency/${consistencyId}`,
        { credentials: 'include' }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.detail || 'Could not load the consistency check status.');
      if (consistencyTokenRef.current !== token) return;
      setConsistencyJob(data);
      if (data.status === 'succeeded') {
        const artifactResponse = await fetch(
          `${CONTRACT_QA_API_BASE}/nda/jobs/${jobId}/consistency/${consistencyId}/artifacts/nda_consistency.json`,
          { credentials: 'include' }
        );
        if (!artifactResponse.ok) throw new Error('Could not load the consistency findings.');
        const payload = await artifactResponse.json();
        if (consistencyTokenRef.current !== token) return;
        setConsistencyResult(payload);
        toast.success('Supporting-document analysis is ready.');
        return;
      }
      if (data.status === 'failed') {
        throw new Error(data.error || 'The consistency check could not be completed.');
      }
    }
    throw new Error('The consistency check took longer than expected. Please try again.');
  };

  const generateRedline = async () => {
    if (!review || !job?.jobId || review.disposition !== 'edit_and_recirculate') return;
    const manualEdits = requiredManualRedlineEdits(review);
    const incomplete = groupedManualRedlineEdits(review).filter((group) => (
      group.edits.some((edit) => !redlineValueIsComplete(edit, redlineValues[edit.editId]))
    ));
    if (incomplete.length) {
      const message = `Complete ${incomplete.length} required administrative field${incomplete.length === 1 ? '' : 's'} before generating the redline.`;
      setRedlineError(message);
      toast.error(message);
      return;
    }

    const token = redlineTokenRef.current + 1;
    redlineTokenRef.current = token;
    setRedlineLoading(true);
    setRedlineError('');
    setRedlineArtifacts([]);
    setRedlineJob({ status: 'queued', stage: 'preflight', progressMessage: 'Preparing the native NDA redline.' });
    try {
      const manualValues = Object.fromEntries(manualEdits.map((edit) => [
        edit.editId,
        redlineValues[edit.editId].trim(),
      ]));
      const response = await fetch(`${CONTRACT_QA_API_BASE}/nda/jobs/${job.jobId}/redlines`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ manualValues }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.detail || 'The native NDA redline could not be started.');
      if (redlineTokenRef.current !== token) return;
      setRedlineJob(data);
      const artifacts = await pollNdaRedline(job.jobId, data.redlineId, token);
      return artifacts ? { redlineId: data.redlineId, artifacts } : null;
    } catch (error) {
      if (redlineTokenRef.current !== token) return;
      const message = userSafeRedlineFailureMessage(error.message);
      setRedlineError(message);
      setRedlineJob((current) => ({ ...(current || {}), status: 'failed', error: message }));
      toast.error(message);
    } finally {
      if (redlineTokenRef.current === token) setRedlineLoading(false);
    }
    return null;
  };

  const pollNdaRedline = async (jobId, redlineId, token) => {
    for (let attempt = 0; attempt < 120; attempt += 1) {
      await delay(attempt === 0 ? 750 : 2000);
      if (redlineTokenRef.current !== token) return;
      const response = await fetch(
        `${CONTRACT_QA_API_BASE}/nda/jobs/${jobId}/redlines/${redlineId}`,
        { credentials: 'include' }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.detail || 'Could not load native redline status.');
      if (redlineTokenRef.current !== token) return;
      setRedlineJob(data);
      if (data.status === 'succeeded') {
        const artifactsResponse = await fetch(
          `${CONTRACT_QA_API_BASE}/nda/jobs/${jobId}/redlines/${redlineId}/artifacts`,
          { credentials: 'include' }
        );
        const artifactsData = await artifactsResponse.json().catch(() => ({}));
        if (!artifactsResponse.ok) throw new Error(artifactsData.detail || 'Could not load the validated redline.');
        setRedlineArtifacts(artifactsData.artifacts || []);
        toast.success('Native NDA redline is ready.');
        return artifactsData.artifacts || [];
      }
      if (data.status === 'failed') {
        throw new Error(data.error || 'The native NDA redline could not be generated.');
      }
    }
    throw new Error('Native redline generation took longer than expected. Please try again.');
  };

  // Unlike the report and JSON exports, the Word memo is rendered server-side from the
  // recorded review, so two people exporting the same NDA send Legal the same document.
  const downloadNdaFindingsWord = async () => {
    if (!job?.jobId || wordExporting) return;
    setWordExporting(true);
    try {
      const response = await fetch(`${CONTRACT_QA_API_BASE}/nda/jobs/${job.jobId}/review/export`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ consistency: consistencyResult || null }),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || data.detail || 'The findings memo could not be exported.');
      }
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = ndaFindingsMemoName(response.headers.get('content-disposition'), job?.counterparty);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    } catch (error) {
      toast.error(error.message || 'The findings memo could not be exported.');
    } finally {
      setWordExporting(false);
    }
  };

  const downloadRedline = async (redlineId = redlineJob?.redlineId, artifacts = redlineArtifacts) => {
    const artifact = findRedlineArtifact(artifacts);
    if (!artifact || !job?.jobId || !redlineId || redlineDownloading) return;
    setRedlineDownloading(true);
    try {
      const response = await fetch(
        `${CONTRACT_QA_API_BASE}/nda/jobs/${job.jobId}/redlines/${redlineId}/artifacts/${encodeURIComponent(artifact.name)}`,
        { credentials: 'include' }
      );
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.detail || 'The validated redline could not be downloaded.');
      }
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = redlineDownloadName(file?.name, artifact.name);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    } catch (error) {
      toast.error(userSafeRedlineFailureMessage(error.message));
    } finally {
      setRedlineDownloading(false);
    }
  };

  // The header's one-click download: the marked-up NDA in the format it was uploaded in.
  // An NDA to recirculate gets its tracked-change redline (generated on demand unless
  // administrative blanks still need values); an escalated NDA gets Legal's commented
  // review copy — Word comments for a .docx, margin notes for a PDF.
  const downloadRedlinedNda = async () => {
    if (!review || !job?.jobId || redlineLoading || redlineDownloading) return;
    const sourceName = file?.name || job?.sourceFilename || '';
    if (review.disposition === 'escalate_to_legal') {
      setRedlineDownloading(true);
      try {
        await downloadNdaReviewCopy(job.jobId, sourceName);
      } catch (error) {
        toast.error(userSafeRedlineFailureMessage(error.message));
      } finally {
        setRedlineDownloading(false);
      }
      return;
    }
    if (findRedlineArtifact(redlineArtifacts) && redlineJob?.redlineId) {
      await downloadRedline();
      return;
    }
    const incomplete = groupedManualRedlineEdits(review).filter((group) => (
      group.edits.some((edit) => !redlineValueIsComplete(edit, redlineValues[edit.editId]))
    ));
    if (incomplete.length) {
      const message = `Fill in ${incomplete.length} required field${incomplete.length === 1 ? '' : 's'} in the Redline Workspace, then download.`;
      setRedlineError(message);
      toast.error(message);
      setCompactPane('results');
      window.setTimeout(() => redlineSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
      return;
    }
    const generated = await generateRedline();
    if (generated) await downloadRedline(generated.redlineId, generated.artifacts);
  };

  const homeLoading = homeTab === 'draft' ? draftLoading : homeTab === 'register' ? registerLoading : loading;
  const draftComplete = draftJob?.status === 'succeeded';

  return (
    <div className="h-full max-h-[calc(100dvh-4rem)] min-h-0 w-full overflow-hidden bg-gray-50 text-gray-900">
      <div className="flex h-full flex-col">
        <header className="shrink-0 border-b border-gray-200 bg-gray-50 px-3 py-3 sm:px-5 sm:py-4 min-[1400px]:px-6">
          <div className="flex flex-col gap-3 min-[1400px]:flex-row min-[1400px]:items-center min-[1400px]:justify-between">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h1 className="text-lg font-bold text-gray-950 sm:text-xl">NDA Agent</h1>
                {(review || !homeLoading) && (
                  <>
                    <span className="hidden text-gray-300 sm:inline">·</span>
                    <p className="min-w-0 truncate text-sm font-medium text-gray-500">
                      {review
                        ? file?.name
                        : homeTab === 'draft'
                          ? 'Create a mutual NDA from the approved Sylvan form'
                          : homeTab === 'register' ? 'Search completed NDA records' : 'Upload an NDA to begin'}
                    </p>
                  </>
                )}
              </div>
            </div>
            <div className="flex w-full shrink-0 flex-col items-end gap-2 min-[1400px]:w-auto min-[1400px]:flex-row min-[1400px]:items-center">
              {!review && !draftComplete && homeLoading && (
                <span className="inline-flex items-center rounded-full border border-orange-200 bg-orange-50 px-3 py-1.5 text-sm font-semibold text-sylvan-700">
                  <Loader2 className="mr-2 animate-spin" size={15} />
                  Processing
                </span>
              )}
              {review && (
                <div className="grid w-full grid-cols-2 gap-2 min-[1400px]:w-auto">
                  <div className="relative min-w-0" ref={exportMenuRef}>
                    <button
                      type="button"
                      onClick={() => setExportMenuOpen((current) => !current)}
                      aria-haspopup="menu"
                      aria-expanded={exportMenuOpen}
                      className="inline-flex min-h-[44px] w-full min-w-0 items-center justify-center rounded-full bg-sylvan-500 px-3 py-2 text-xs font-semibold text-white shadow-sm hover:bg-sylvan-600 sm:px-4 sm:text-sm"
                    >
                      {redlineLoading || redlineDownloading || wordExporting
                        ? <Loader2 className="mr-2 shrink-0 animate-spin" size={15} />
                        : <Download className="mr-2 shrink-0" size={15} />}
                      Download the Report
                      <ChevronDown className="ml-1.5 shrink-0" size={14} />
                    </button>
                    {exportMenuOpen && (
                      <div className="absolute right-0 z-30 mt-2 w-72 rounded-lg border border-gray-200 bg-white p-1 shadow-lg" role="menu" aria-label="Download the report">
                        <button
                          type="button"
                          role="menuitem"
                          disabled={!ndaHasRedline(review) || redlineLoading || redlineDownloading}
                          onClick={() => {
                            setExportMenuOpen(false);
                            downloadRedlinedNda();
                          }}
                          className="flex w-full items-start gap-2 rounded-md px-3 py-2 text-left text-sm text-gray-900 hover:bg-orange-50 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:bg-transparent"
                        >
                          <Edit3 className="mt-0.5 shrink-0 text-sylvan-600" size={15} />
                          <span>
                            <span className="block font-semibold">Download Redline</span>
                            <span className="block text-xs text-gray-500">{ndaRedlineMenuNote(review, file?.name || job?.sourceFilename)}</span>
                          </span>
                        </button>
                        <button
                          type="button"
                          role="menuitem"
                          disabled={wordExporting || !job?.jobId}
                          onClick={() => {
                            setExportMenuOpen(false);
                            downloadNdaFindingsWord();
                          }}
                          className="flex w-full items-start gap-2 rounded-md px-3 py-2 text-left text-sm text-gray-900 hover:bg-orange-50 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          <FileText className="mt-0.5 shrink-0 text-sylvan-600" size={15} />
                          <span>
                            <span className="block font-semibold">Download the Findings</span>
                            <span className="block text-xs text-gray-500">Rule-by-rule results as a Word document</span>
                          </span>
                        </button>
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                      onClick={historicalDetail ? backToRegister : resetReview}
                    className="inline-flex min-h-[44px] min-w-0 items-center justify-center rounded-full border border-orange-200 bg-white px-3 py-2 text-xs font-semibold text-sylvan-700 shadow-sm hover:bg-orange-50 sm:px-4 sm:text-sm"
                  >
                    {historicalDetail ? <ArrowLeft className="mr-2 shrink-0" size={15} /> : <RefreshCw className="mr-2 shrink-0" size={15} />}
                    {historicalDetail ? 'Back to NDA Register' : 'Review another NDA'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {!review ? (
          <main className="min-h-0 flex-1 overflow-y-auto px-3 py-5 sm:px-6 sm:py-8">
            <div className={`mx-auto w-full space-y-5 ${homeTab === 'register' ? 'max-w-6xl' : 'max-w-3xl'}`}>
              {(!homeLoading || homeTab === 'register') && !draftComplete && (
                <NdaHomeTabs
                  active={homeTab}
                  onChange={(nextTab) => {
                    setHistoricalDetail(null);
                    setHomeTab(nextTab);
                    if (nextTab === 'review') resetDraft();
                    else if (nextTab === 'draft') resetReview();
                    else {
                      resetReview();
                      resetDraft();
                    }
                  }}
                />
              )}
              {homeTab === 'review' ? (
                loading ? (
                  <NdaProcessingCard job={job} />
                ) : (
                  <>
                    {job?.status === 'failed' && (
                      <div className="flex items-start gap-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-4 text-sm text-rose-900" role="alert">
                        <AlertTriangle className="mt-0.5 shrink-0" size={18} />
                        <div>
                          <p className="font-bold">NDA review failed</p>
                          <p className="mt-1 leading-6">{job.error}</p>
                        </div>
                      </div>
                    )}
                    <NdaIntakePanel
                      file={file}
                      informationFlow={informationFlow}
                      supportingFiles={consistencyFiles}
                      onSupportingFilesChange={(nextFiles) => {
                        setConsistencyFiles(nextFiles);
                        setConsistencyError('');
                      }}
                      onFileChange={setFile}
                      onInformationFlowChange={setInformationFlow}
                      onStart={startReview}
                    />
                  </>
                )
              ) : homeTab === 'register' ? (
                <NdaRegisterPanel
                  entries={registerEntries}
                  pagination={registerPagination}
                  filters={registerFilters}
                  loading={registerLoading}
                  error={registerError}
                  onFiltersChange={setRegisterFilters}
                  onSearch={() => loadRegister(1)}
                  onClear={() => {
                    const cleared = { query: '', entryType: '', disposition: '' };
                    setRegisterFilters(cleared);
                    loadRegister(1, cleared);
                  }}
                  onPageChange={(page) => loadRegister(page)}
                  onOpen={openRegisterEntry}
                />
              ) : draftLoading ? (
                <NdaDraftProcessingCard job={draftJob} />
              ) : draftComplete ? (
                <>
                  {historicalDetail && (
                    <NdaHistoricalRecordDetails
                      detail={historicalDetail}
                      downloading={registerDownload}
                      onDownload={downloadRegisterArtifact}
                    />
                  )}
                  <NdaDraftSuccessPanel
                    form={draftForm}
                    downloading={draftDownloading || Boolean(registerDownload)}
                    onDownload={historicalDetail ? () => {
                      const artifact = historicalDetail.artifacts?.find((item) => item.name === 'sylvan_nda_draft.docx');
                      if (artifact) downloadRegisterArtifact(artifact.url, artifact.name);
                    } : downloadDraft}
                    onReset={historicalDetail ? backToRegister : resetDraft}
                    resetLabel={historicalDetail ? 'Back to NDA Register' : 'Draft another NDA'}
                  />
                </>
              ) : (
                <>
                  {draftError && (
                    <div className="flex items-start gap-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-4 text-sm text-rose-900" role="alert">
                      <AlertTriangle className="mt-0.5 shrink-0" size={18} />
                      <div>
                        <p className="font-bold">NDA draft was not generated</p>
                        <p className="mt-1 leading-6">{draftError}</p>
                      </div>
                    </div>
                  )}
                  <NdaDraftIntakePanel
                    form={draftForm}
                    onChange={(field, value) => {
                      setDraftForm((current) => ({ ...current, [field]: value }));
                      setDraftError('');
                    }}
                    onGenerate={generateDraft}
                  />
                </>
              )}
            </div>
          </main>
        ) : (
          <>
            <div className="grid shrink-0 grid-cols-2 border-b border-gray-200 bg-white p-1 min-[1400px]:hidden" role="tablist" aria-label="NDA review view">
              <MobilePaneButton
                active={compactPane === 'results'}
                icon={<FileText size={16} />}
                label="Results"
                onClick={() => setCompactPane('results')}
              />
              <MobilePaneButton
                active={compactPane === 'chat'}
                icon={<MessageSquare size={16} />}
                label="AI Assistant"
                onClick={() => setCompactPane('chat')}
              />
            </div>
            <div className={`grid min-h-0 flex-1 grid-cols-1 overflow-hidden ${assistantExpanded ? '' : 'min-[1400px]:grid-cols-[minmax(0,1fr)_420px]'}`}>
              {!assistantExpanded && <main className={`${compactPane === 'results' ? 'flex' : 'hidden'} min-h-0 min-w-0 flex-col overflow-x-hidden overflow-y-auto px-3 py-4 sm:px-5 sm:py-5 min-[1400px]:flex min-[1400px]:px-6 min-[1400px]:py-6`}>
                {historicalDetail && (
                  <NdaHistoricalRecordDetails
                    detail={historicalDetail}
                    downloading={registerDownload}
                    onDownload={downloadRegisterArtifact}
                  />
                )}
                <NdaWorkspace job={job} review={review} fileName={file?.name} />
                {review.disposition === 'edit_and_recirculate' && (
                  <div ref={redlineSectionRef} className="scroll-mt-4">
                    <NdaRedlineWorkspace
                      review={review}
                      values={redlineValues}
                      onValueChange={(editId, value) => {
                        setRedlineValues((current) => ({ ...current, [editId]: value }));
                        setRedlineError('');
                      }}
                      onGenerate={generateRedline}
                      onDownload={() => downloadRedline()}
                      job={redlineJob}
                      artifacts={redlineArtifacts}
                      loading={redlineLoading}
                      downloading={redlineDownloading}
                      error={redlineError}
                    />
                  </div>
                )}
                <NdaConsistencySection
                  files={consistencyFiles}
                  onFilesChange={(nextFiles) => {
                    setConsistencyFiles(nextFiles);
                    setConsistencyError('');
                  }}
                  onRun={runConsistencyCheck}
                  job={consistencyJob}
                  result={consistencyResult}
                  loading={consistencyLoading}
                  error={consistencyError}
                  onReset={() => {
                    consistencyTokenRef.current += 1;
                    setConsistencyFiles([]);
                    setConsistencyJob(null);
                    setConsistencyResult(null);
                    setConsistencyError('');
                    setConsistencyLoading(false);
                  }}
                />
              </main>}
              <NdaAssistant
                expanded={assistantExpanded}
                visible={compactPane === 'chat'}
                onToggleExpanded={() => setAssistantExpanded((current) => !current)}
                messages={messages}
                input={input}
                loading={chatLoading}
                ruleCatalog={review.ruleCatalog}
                onInputChange={setInput}
                onKeyDown={handleKeyDown}
                onSubmit={() => submitQuestion()}
                onPromptClick={submitQuestion}
                messagesEndRef={messagesEndRef}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const userSafeNdaFailureMessage = (message, code) => {
  if (code === 'AI_PROVIDER_UNAVAILABLE') {
    return 'The AI review service is temporarily busy. Please try again in a few minutes.';
  }
  const value = String(message || '');
  const normalized = value.toLowerCase();
  const internalProviderError = [
    'overloaded_error',
    'request_id',
    'did not produce valid output',
    'invalid json',
    "'type': 'error'",
    '"type": "error"',
  ].some((marker) => normalized.includes(marker));
  if (internalProviderError) {
    return normalized.includes('overload')
      ? 'The AI review service is temporarily busy. Please try again in a few minutes.'
      : 'The NDA review could not be completed. Please try again.';
  }
  return value || 'The NDA review could not be completed. Please try again.';
};

const NdaRedlineWorkspace = ({
  review,
  values,
  onValueChange,
  onGenerate,
  onDownload,
  job,
  artifacts,
  loading,
  downloading,
  error,
}) => {
  const manualEdits = requiredManualRedlineEdits(review);
  const editGroups = groupedManualRedlineEdits(review);
  const redlineArtifact = artifacts.find((item) => /(?:nda_redline|nda_review_copy)\.(?:docx|pdf)$/i.test(item.name));
  const complete = manualEdits.every((edit) => redlineValueIsComplete(edit, values[edit.editId]));
  const completedGroups = editGroups.filter((group) => (
    group.edits.every((edit) => redlineValueIsComplete(edit, values[edit.editId]))
  ));
  const manualEditIds = new Set(manualEdits.map((edit) => edit.editId));
  const approvedEdits = (review?.proposedEdits || []).filter((edit) => !manualEditIds.has(edit.editId));
  const pagesAffected = [...new Set((review?.proposedEdits || [])
    .map((edit) => edit.anchor?.pageNumber)
    .filter(Boolean))].sort((a, b) => a - b);

  return (
    <section className="mt-6 shrink-0 border-t border-gray-200 pt-6" aria-labelledby="nda-redline-title">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-3">
          <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-orange-50 text-sylvan-600">
            <Edit3 size={18} />
          </span>
          <div>
            <h2 id="nda-redline-title" className="font-bold text-gray-950">Redline Workspace</h2>
            <p className="mt-1 text-sm leading-6 text-gray-600">
              Produces a copy of the original document with every routine edit as a tracked change you can
              accept or reject in Word, and every review finding as a margin comment on the clause it concerns.
            </p>
          </div>
        </div>
        {job?.status && (
          <span className={`inline-flex w-fit shrink-0 items-center rounded-full px-3 py-1 text-xs font-bold ${
            job.status === 'succeeded'
              ? 'bg-emerald-100 text-emerald-800'
              : job.status === 'failed'
                ? 'bg-rose-100 text-rose-800'
                : 'bg-orange-50 text-sylvan-700'
          }`}>
            {loading && <Loader2 className="mr-1.5 animate-spin" size={13} />}
            {redlineStatusLabel(job.status)}
          </span>
        )}
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <RedlineKpi value={approvedEdits.length} label="Approved edits" />
        <RedlineKpi value={editGroups.length} label="Values needed" />
        <RedlineKpi value={`${completedGroups.length} / ${editGroups.length}`} label="Values complete" />
        <RedlineKpi value={pagesAffected.length ? pagesAffected.join(', ') : '—'} label="Pages affected" />
      </div>

      <div className="mt-4 space-y-4">
        {manualEdits.length > 0 && job?.status !== 'succeeded' && (
          <div className="rounded-xl border border-gray-200 bg-white p-4 sm:p-5">
            <div className="flex items-center justify-between gap-3">
              <h3 className="text-xs font-bold uppercase tracking-wide text-gray-600">Required Administrative Values</h3>
              <span className="shrink-0 text-xs font-semibold text-gray-500">
                {completedGroups.length} of {editGroups.length} complete
              </span>
            </div>
            <p className="mt-1 text-sm leading-6 text-gray-600">
              The document left these fields blank. Enter the verified values; each becomes a tracked replacement at its location in the NDA.
            </p>
            <div className="mt-3 space-y-3">
              {editGroups.map((group) => {
                const lead = group.edits[0];
                const value = values[lead.editId] || '';
                const issue = redlineValueIssue(lead, value);
                const typedButInvalid = Boolean(issue) && Boolean(value.trim());
                const inPlace = lead.anchor?.placement === 'replace' && !anchorIsPureBlank(lead);
                return (
                  <div
                    key={lead.editId}
                    className={`rounded-lg border p-3 sm:p-4 ${issue ? 'border-gray-200 bg-white' : 'border-emerald-200 bg-emerald-50/40'}`}
                  >
                    <div className="flex items-start gap-3">
                      <span className={`mt-0.5 shrink-0 ${issue ? 'text-gray-300' : 'text-emerald-600'}`} aria-hidden="true">
                        {issue ? <Circle size={18} /> : <CheckCircle2 size={18} />}
                      </span>
                      <div className="min-w-0 flex-1">
                        <label htmlFor={`redline-${lead.editId}`} className="text-sm font-semibold text-gray-900">
                          {group.label} <span className="text-red-600">*</span>
                        </label>
                        <p className="mt-1 text-xs leading-5 text-gray-500">{lead.rationale}</p>
                        <textarea
                          id={`redline-${lead.editId}`}
                          rows={value.length > 120 ? 3 : 2}
                          value={value}
                          maxLength={2000}
                          placeholder={inPlace ? undefined : manualRedlinePlaceholder(group.label)}
                          onChange={(event) => group.edits.forEach((edit) => onValueChange(edit.editId, event.target.value))}
                          className={`mt-2 w-full resize-y rounded-lg border px-3 py-2 text-sm leading-6 text-gray-900 outline-none focus:ring-2 ${
                            typedButInvalid
                              ? 'border-amber-400 bg-amber-50/40 focus:border-amber-500 focus:ring-amber-100'
                              : 'border-gray-300 bg-white focus:border-sylvan-400 focus:ring-orange-100'
                          }`}
                          aria-invalid={Boolean(issue)}
                        />
                        <p className="mt-1 text-xs leading-5 text-gray-500">
                          {inPlace ? 'Edit the document text above in place.' : manualRedlineLocationNote(group)}
                        </p>
                        {typedButInvalid && (
                          <p className="mt-1 text-xs font-medium text-amber-800">{issue}</p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {loading && (
          <div className="flex items-start gap-3 rounded-lg border border-orange-200 bg-orange-50 px-4 py-3 text-sm text-orange-950" aria-live="polite">
            <Loader2 className="mt-0.5 shrink-0 animate-spin" size={17} />
            <div>
              <p className="font-bold">Generating and validating redline</p>
              <p className="mt-1 leading-5">{job?.progressMessage || 'Preparing the native document edits.'}</p>
            </div>
          </div>
        )}

        {error && !loading && (
          <div className="flex items-start gap-3 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-900" role="alert">
            <AlertTriangle className="mt-0.5 shrink-0" size={17} />
            <div>
              <p className="font-bold">Redline was not generated</p>
              <p className="mt-1 leading-5">{error}</p>
            </div>
          </div>
        )}

        {job?.status === 'succeeded' && redlineArtifact && (
          <div className="flex flex-col gap-4 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="mt-0.5 shrink-0 text-emerald-700" size={19} />
              <div>
                <p className="font-bold text-emerald-950">Validated native redline ready</p>
                <p className="mt-1 text-sm leading-5 text-emerald-900">All approved edits were placed and the generated document passed validation.</p>
              </div>
            </div>
            <button
              type="button"
              onClick={onDownload}
              disabled={downloading}
              className="inline-flex min-h-[44px] shrink-0 items-center justify-center rounded-lg bg-sylvan-500 px-4 py-2 text-sm font-semibold text-white hover:bg-sylvan-600 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {downloading ? <Loader2 className="mr-2 animate-spin" size={16} /> : <Download className="mr-2" size={16} />}
              Download Redline
            </button>
          </div>
        )}

        {job?.status !== 'succeeded' && !loading && (
          <div className="flex flex-col gap-3 border-t border-gray-100 pt-4 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-xs leading-5 text-gray-500">No partial or unvalidated document will be published.</p>
            <button
              type="button"
              onClick={onGenerate}
              disabled={!complete}
              className="inline-flex min-h-[44px] items-center justify-center rounded-lg bg-sylvan-500 px-5 py-2 text-sm font-semibold text-white hover:bg-sylvan-600 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Edit3 className="mr-2" size={16} />
              {job?.status === 'failed' ? 'Try Again' : 'Generate Redline'}
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

const RedlineKpi = ({ value, label }) => (
  <div className="rounded-xl border border-gray-200 bg-white px-4 py-3">
    <span className="block truncate text-xl font-bold text-gray-950">{value}</span>
    <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">{label}</span>
  </div>
);

const escapeNdaHtml = (value) => String(value ?? '').replace(/[&<>"']/g, (character) => ({
  '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
}[character]));

const ndaExportStatusLabel = (result) => {
  if (!result) return 'Not evaluated';
  if (result.consequence === 'escalate_to_legal' || result.status === 'uncertain') return 'Legal review';
  if (result.consequence === 'routine_edit') return 'Edit required';
  if (result.status === 'not_found') return 'Not found';
  if (result.status === 'not_applicable') return 'Not applicable';
  return 'Pass';
};

const ndaExportOperationLabel = (operation) => ({
  insert: 'Insert approved language',
  replace: 'Replace with approved language',
  delete: 'Delete language',
  manual_fill: 'Complete administrative field',
}[operation] || String(operation || 'Routine correction').replace(/_/g, ' '));

const NDA_EXPORT_DISCLAIMER = 'Guide eligibility is not legal approval, signature authority, or authorization to bind Sylvan. This report was generated by the Sylvan NDA Agent for internal review.';

export const buildNdaFindingsReportHtml = (review, { fileName, jobId, exportedAt, consistency }) => {
  const disposition = ndaDispositionDisplay(review.disposition);
  const assessment = review.documentAssessment || {};
  const edits = review.proposedEdits || [];
  const triggers = review.escalationTriggers || [];
  const warnings = review.warnings || [];
  const uncertainties = review.uncertainties || [];
  const resultByRule = new Map((review.ruleResults || []).map((result) => [result.ruleId, result]));
  const ruleById = new Map((review.ruleCatalog || []).map((rule) => [rule.id, rule]));
  // The report reads the same findings model the screen does, so a trigger carries
  // its reasoning and quoted source here exactly as it does on screen.
  const escalationActions = buildNdaEscalationActions(review);
  const legalReviewReasons = buildNdaLegalReviewReasons(review, escalationActions);
  const scope = ndaScopeDisplay(review);
  const unusualFillIns = review.unusualFillIns || [];
  const findingItems = (items) => items.map((item) => {
    const evidence = (item.evidence || [])
      .filter((entry) => entry.exactQuote)
      .map((entry) => `<div class="evidence"><span class="muted">${escapeNdaHtml(formatNdaPages(entry.pageNumbers))}</span> &ldquo;${escapeNdaHtml(entry.exactQuote)}&rdquo;</div>`)
      .join('');
    const absence = item.absenceCheck?.completeDocumentReviewed
      ? '<div class="muted">Complete document checked</div>'
      : '';
    return `<li>
      <strong>${escapeNdaHtml(item.title)}</strong>${item.severity ? ` <span class="muted">&middot; ${escapeNdaHtml(formatNdaSeverity(item.severity))}</span>` : ''}
      <div>${escapeNdaHtml(item.description || '')}</div>
      ${evidence}${absence}
    </li>`;
  }).join('');
  const confidence = (value) => (typeof value === 'number' ? ` (${Math.round(value * 100)}% confidence)` : '');
  const flowLabel = { mutual: 'Mutual', sylvan_discloses: 'Sylvan discloses', counterparty_discloses: 'Sylvan receives' }[
    assessment.inferredInformationFlow
  ] || 'Not determined';

  const checklistRows = (review.ruleCatalog || []).map((rule) => {
    const result = resultByRule.get(rule.id);
    const evidence = (result?.evidence || [])
      .filter((item) => item.exactQuote)
      .map((item) => `<div class="evidence">&ldquo;${escapeNdaHtml(item.exactQuote)}&rdquo;${
        item.pageNumbers?.length ? ` <span class="muted">(page ${item.pageNumbers.join(', ')})</span>` : ''
      }</div>`)
      .join('');
    return `<tr>
      <td class="rule-id">${escapeNdaHtml(rule.id)}</td>
      <td><strong>${escapeNdaHtml(rule.title)}</strong>${result?.rationale ? `<div class="muted">${escapeNdaHtml(result.rationale)}</div>` : ''}${evidence}</td>
      <td class="status">${escapeNdaHtml(ndaExportStatusLabel(result))}</td>
    </tr>`;
  }).join('');

  // Lead with the rule title, as the screen does: "Insert approved language" alone
  // does not tell a reviewer which provision it belongs to.
  const editItems = edits.map((edit) => `<li>
    <strong>${escapeNdaHtml(ruleById.get(edit.authorizationRuleId)?.title || edit.authorizationRuleId || 'Routine correction')}</strong> &middot; <span class="muted">${escapeNdaHtml(ndaExportOperationLabel(edit.operation))}${edit.authorizationRuleId ? ` &middot; ${escapeNdaHtml(edit.authorizationRuleId)}` : ''}${edit.anchor?.pageNumber ? ` &middot; page ${edit.anchor.pageNumber}` : ''}${ndaRuleSeverity(review, edit.authorizationRuleId) ? ` &middot; ${escapeNdaHtml(formatNdaSeverity(ndaRuleSeverity(review, edit.authorizationRuleId)))}` : ''}</span>
    <div>${escapeNdaHtml(edit.rationale || '')}</div>
    ${edit.approvedText ? `<div class="replacement"><div class="replacement-label">Recommended replacement</div>${
      formatNdaClauseLines(edit.approvedText)
        .map((line) => `<div${isNdaEnumeratedLine(line) ? ' class="clause-item"' : ''}>${escapeNdaHtml(line)}</div>`)
        .join('')
    }</div>` : ''}
  </li>`).join('');

  const listItems = (values) => values.map((value) => `<li>${escapeNdaHtml(value)}</li>`).join('');

  const consistencyPosition = (label, position) => {
    const quote = position?.exact_quote
      ? `<div class="evidence">${escapeNdaHtml(position.exact_quote)}${
        position.page_numbers?.length ? ` <span class="muted">(page ${position.page_numbers.join(', ')})</span>` : ''
      }</div>`
      : '';
    return `<div><span class="muted">${escapeNdaHtml(label)}:</span> ${escapeNdaHtml(position?.summary || '')}</div>${quote}`;
  };
  const consistencySection = consistency?.findings?.length
    ? `<h2>Supporting document consistency (advisory)</h2>
      <p class="muted">${escapeNdaHtml(consistency.disclaimer || 'Advisory only; these findings do not change the review result.')}</p>
      <ul>${consistency.findings.map((finding) => `<li>
        <strong>${escapeNdaHtml((finding.severity || '').toUpperCase())}</strong>: ${escapeNdaHtml(finding.topic || '')}
        <span class="muted">&middot; ${escapeNdaHtml(finding.document_filename || '')}</span>
        ${consistencyPosition('NDA', finding.nda_position)}
        ${consistencyPosition('Supporting document', finding.supporting_position)}
        <div class="muted">${escapeNdaHtml(finding.rationale || '')}</div>
      </li>`).join('')}</ul>`
    : '';

  return `<!doctype html><html><head><meta charset="utf-8"><title>NDA Review Findings</title><style>
    @page { margin: 0.9in; }
    body { font: 13px/1.55 -apple-system, "Segoe UI", Roboto, Arial, sans-serif; color: #111827; margin: 0; }
    h1 { font-size: 21px; margin: 0; }
    h2 { font-size: 14px; text-transform: uppercase; letter-spacing: 0.4px; color: #374151; border-bottom: 1px solid #e5e7eb; padding-bottom: 4px; margin: 24px 0 10px; }
    .brand { color: #9a3412; font-weight: 700; font-size: 12px; letter-spacing: 0.6px; text-transform: uppercase; }
    table.meta { border-collapse: collapse; margin-top: 10px; font-size: 12.5px; }
    table.meta td { padding: 2px 14px 2px 0; vertical-align: top; }
    table.meta td:first-child { color: #6b7280; white-space: nowrap; }
    .disposition { border: 1px solid #d1d5db; border-left: 5px solid #9a3412; border-radius: 6px; padding: 10px 14px; margin-top: 16px; }
    .disposition strong { font-size: 15px; }
    .metrics { display: flex; gap: 24px; margin-top: 10px; font-size: 12.5px; color: #374151; }
    table.checklist { width: 100%; border-collapse: collapse; font-size: 12.5px; }
    table.checklist th { text-align: left; border-bottom: 1.5px solid #9ca3af; padding: 5px 8px; font-size: 11px; text-transform: uppercase; color: #6b7280; }
    table.checklist td { border-bottom: 1px solid #e5e7eb; padding: 6px 8px; vertical-align: top; }
    tr { break-inside: avoid; }
    li { margin: 6px 0; break-inside: avoid; }
    .rule-id { white-space: nowrap; color: #6b7280; }
    .status { white-space: nowrap; font-weight: 600; }
    .muted { color: #6b7280; }
    .evidence { border-left: 3px solid #e5e7eb; margin-top: 4px; padding: 2px 10px; color: #374151; font-style: italic; white-space: pre-wrap; }
    .replacement { border: 1px solid #e5e7eb; border-radius: 6px; margin-top: 6px; padding: 8px 12px; color: #1f2937; break-inside: avoid; }
    .replacement-label { font-size: 10.5px; text-transform: uppercase; letter-spacing: 0.04em; font-weight: 700; color: #6b7280; margin-bottom: 4px; }
    .replacement div + div { margin-top: 4px; }
    .clause-item { padding-left: 18px; }
    .callout { border: 1px solid #fcd34d; background: #fffbeb; border-radius: 6px; padding: 10px 14px; margin-top: 14px; color: #78350f; }
    .callout ul { margin: 4px 0 6px; }
    .disclaimer { margin-top: 28px; border-top: 1px solid #e5e7eb; padding-top: 10px; font-size: 11px; color: #6b7280; }
  </style></head><body>
    <div class="brand">FM Sylvan &middot; NDA Agent</div>
    <h1>NDA Review Findings</h1>
    <table class="meta">
      <tr><td>Document</td><td>${escapeNdaHtml(fileName || 'NDA document')}</td></tr>
      <tr><td>Counterparty</td><td>${escapeNdaHtml(assessment.derivedCounterparty || 'Not determined')}${confidence(assessment.counterpartyConfidence)}</td></tr>
      <tr><td>Stated purpose</td><td>${escapeNdaHtml(assessment.documentStatedPurpose || 'Not determined')}</td></tr>
      <tr><td>Information flow</td><td>${escapeNdaHtml(flowLabel)}</td></tr>
      ${scope ? `<tr><td>NDA scope</td><td>${escapeNdaHtml(scope.label)} &middot; ${escapeNdaHtml(scope.effect)}${
        scope.rationale ? `<div class="muted">${escapeNdaHtml(scope.rationale)}</div>` : ''}${
        scope.note ? `<div class="muted">${escapeNdaHtml(scope.note)}</div>` : ''}</td></tr>` : ''}
      <tr><td>Review job</td><td>${escapeNdaHtml(jobId || 'Not recorded')}</td></tr>
      <tr><td>Exported</td><td>${escapeNdaHtml(exportedAt)}</td></tr>
    </table>
    <div class="disposition">
      <strong>${escapeNdaHtml(disposition.label)}</strong>
      <div>${escapeNdaHtml(disposition.guidance)}</div>
      <div class="metrics">
        <span>Required edits: <strong>${edits.length}</strong></span>
        <span>Legal triggers: <strong>${triggers.length}</strong></span>
        <span>Warnings: <strong>${warnings.length}</strong></span>
        <span>Checks run: <strong>${(review.ruleResults || []).length}</strong></span>
      </div>
    </div>
    ${unusualFillIns.length ? `<div class="callout"><strong>${
      unusualFillIns.length === 1 ? 'Unusual field to complete before signing' : 'Unusual fields to complete before signing'
    }</strong><ul>${unusualFillIns.map((field) => {
      const where = describeNdaFillInLocation(field);
      return `<li>&ldquo;${escapeNdaHtml(field.label)}&rdquo;${where ? ` &mdash; ${escapeNdaHtml(where)}` : ''}${
        field.after ? `<div class="muted">Right after: ${escapeNdaHtml(field.after)}</div>` : ''}${
        field.line ? `<div>As printed: ${escapeNdaHtml(field.line)}</div>` : ''}</li>`;
    }).join('')}</ul>
      <div>This is not a standard NDA field and may add substantive terms. Confirm with the requester what it should say before signing.</div></div>` : ''}
    ${escalationActions.length ? `<h2>Legal escalation triggers</h2><ul>${findingItems(escalationActions)}</ul>` : ''}
    ${legalReviewReasons.length ? `<h2>Reason for Legal Review</h2><ul>${findingItems(legalReviewReasons)}</ul>` : ''}
    ${edits.length ? `<h2>Required edits</h2><ul>${editItems}</ul>` : ''}
    ${warnings.length ? `<h2>Warnings</h2><ul>${listItems(warnings)}</ul>` : ''}
    ${uncertainties.length ? `<h2>Unresolved uncertainties</h2><ul>${listItems(uncertainties)}</ul>` : ''}
    <h2>Full checklist</h2>
    <table class="checklist">
      <thead><tr><th>Rule</th><th>Finding</th><th>Status</th></tr></thead>
      <tbody>${checklistRows}</tbody>
    </table>
    ${consistencySection}
    <div class="disclaimer">${escapeNdaHtml(NDA_EXPORT_DISCLAIMER)}</div>
  </body></html>`;
};

const NDA_CONSISTENCY_SEVERITIES = {
  contradiction: { label: 'Contradiction', chip: 'bg-rose-100 text-rose-800', border: 'border-rose-200' },
  tension: { label: 'Tension', chip: 'bg-amber-100 text-amber-800', border: 'border-amber-200' },
  consistent: { label: 'Consistent', chip: 'bg-emerald-100 text-emerald-800', border: 'border-emerald-200' },
};

const NdaConsistencySection = ({ files, onFilesChange, onRun, job, result, loading, error, onReset }) => {
  const running = loading || ['queued', 'running'].includes(job?.status);
  const findings = result?.findings || [];
  const severityCount = (severity) => findings.filter((item) => item.severity === severity).length;
  return (
    <section className="mt-6 shrink-0 border-t border-gray-200 pt-6" aria-labelledby="nda-consistency-title">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-3">
          <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-orange-50 text-sylvan-600">
            <Search size={18} />
          </span>
          <div>
            <h2 id="nda-consistency-title" className="font-bold text-gray-950">Supporting Document Consistency</h2>
            <p className="mt-1 text-sm leading-6 text-gray-600">
              Compare proposals, bid documents, or contract exhibits against this NDA to surface contradictions in language or terms.
            </p>
          </div>
        </div>
        <span className="inline-flex w-fit shrink-0 items-center rounded-full bg-gray-100 px-3 py-1 text-xs font-bold text-gray-600">
          Advisory only
        </span>
      </div>
      <p className="mt-2 text-xs leading-5 text-gray-500">
        Findings inform the reviewer; they do not change the review result above and never route an NDA to Legal on their own.
      </p>

      <div className="mt-4 space-y-4">
        {!result && !running && (
          <div className="rounded-xl border border-gray-200 bg-white p-4 sm:p-5">
            <label htmlFor="nda-consistency-files" className="text-sm font-semibold text-gray-900">
              Supporting documents <span className="text-gray-500">(up to 5 PDF or DOCX files)</span>
            </label>
            <input
              id="nda-consistency-files"
              type="file"
              multiple
              accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              onChange={(event) => {
                const selected = Array.from(event.target.files || []);
                onFilesChange([...files, ...selected].slice(0, 5));
                event.target.value = '';
              }}
              className="mt-2 block w-full text-sm text-gray-700 file:mr-3 file:rounded-full file:border-0 file:bg-orange-50 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-sylvan-700 hover:file:bg-orange-100"
            />
            {files.length > 0 && (
              <ul className="mt-3 space-y-2">
                {files.map((item, index) => (
                  <li key={`${item.name}-${index}`} className="flex items-center justify-between rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-800">
                    <span className="min-w-0 truncate">{item.name}</span>
                    <button
                      type="button"
                      onClick={() => onFilesChange(files.filter((_, itemIndex) => itemIndex !== index))}
                      className="ml-3 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100"
                      aria-label={`Remove ${item.name}`}
                    >
                      <X size={14} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-xs leading-5 text-gray-500">Each document is analyzed with AI; expect a few minutes per file.</p>
              <button
                type="button"
                onClick={onRun}
                disabled={!files.length}
                className="inline-flex min-h-[44px] items-center justify-center rounded-lg bg-sylvan-500 px-5 py-2 text-sm font-semibold text-white hover:bg-sylvan-600 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Search className="mr-2" size={16} />
                Run Consistency Check
              </button>
            </div>
          </div>
        )}

        {running && (
          <div className="flex items-start gap-3 rounded-lg border border-orange-200 bg-orange-50 px-4 py-3 text-sm text-orange-950" aria-live="polite">
            <Loader2 className="mt-0.5 shrink-0 animate-spin" size={17} />
            <div>
              <p className="font-bold">Analyzing supporting documents</p>
              <p className="mt-1 leading-5">{job?.progressMessage || 'Preparing the supporting-document consistency check.'}</p>
            </div>
          </div>
        )}

        {error && !running && (
          <div className="flex items-start gap-3 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-900" role="alert">
            <AlertTriangle className="mt-0.5 shrink-0" size={17} />
            <div>
              <p className="font-bold">Consistency check was not completed</p>
              <p className="mt-1 leading-5">{error}</p>
            </div>
          </div>
        )}

        {result && (
          <>
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
              <RedlineKpi value={severityCount('contradiction')} label="Contradictions" />
              <RedlineKpi value={severityCount('tension')} label="Tensions" />
              <RedlineKpi value={severityCount('consistent')} label="Confirmations" />
              <RedlineKpi value={(result.documents || []).length} label="Documents" />
            </div>
            {(result.documents || []).map((document) => (
              <div key={document.documentIndex} className="rounded-xl border border-gray-200 bg-white p-4 sm:p-5">
                <div className="flex items-center justify-between gap-3">
                  <h3 className="min-w-0 truncate text-xs font-bold uppercase tracking-wide text-gray-600">{document.filename}</h3>
                  <span className="shrink-0 text-xs font-semibold text-gray-500">{(document.findings || []).length} finding{(document.findings || []).length === 1 ? '' : 's'}</span>
                </div>
                {document.documentSummary && (
                  <p className="mt-1 text-sm leading-6 text-gray-600">{document.documentSummary}</p>
                )}
                <div className="mt-3 space-y-3">
                  {(document.findings || []).map((finding) => {
                    const severity = NDA_CONSISTENCY_SEVERITIES[finding.severity] || NDA_CONSISTENCY_SEVERITIES.tension;
                    return (
                      <div key={finding.finding_id} className={`rounded-lg border p-3 sm:p-4 ${severity.border}`}>
                        <div className="flex items-start justify-between gap-3">
                          <p className="text-sm font-semibold text-gray-900">{finding.topic}</p>
                          <span className={`shrink-0 rounded-full px-2.5 py-0.5 text-xs font-bold ${severity.chip}`}>{severity.label}</span>
                        </div>
                        <div className="mt-2 space-y-2 text-sm leading-6 text-gray-800">
                          <p><span className="font-semibold text-gray-600">NDA:</span> {finding.nda_position?.summary}</p>
                          {finding.nda_position?.exact_quote && (
                            <p className="border-l-2 border-gray-200 pl-3 text-gray-600 italic">
                              &ldquo;{finding.nda_position.exact_quote}&rdquo;
                              {finding.nda_position.page_numbers?.length ? ` (page ${finding.nda_position.page_numbers.join(', ')})` : ''}
                            </p>
                          )}
                          <p><span className="font-semibold text-gray-600">{document.filename}:</span> {finding.supporting_position?.summary}</p>
                          {finding.supporting_position?.exact_quote && (
                            <p className="border-l-2 border-gray-200 pl-3 text-gray-600 italic">
                              &ldquo;{finding.supporting_position.exact_quote}&rdquo;
                              {finding.supporting_position.page_numbers?.length ? ` (page ${finding.supporting_position.page_numbers.join(', ')})` : ''}
                            </p>
                          )}
                          <p className="text-gray-600">{finding.rationale}</p>
                          {finding.quotes_verified === false && (
                            <p className="text-xs font-medium text-amber-800">A quoted passage could not be verified verbatim against the source text.</p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  {!(document.findings || []).length && (
                    <p className="text-sm text-gray-600">No notable findings for this document.</p>
                  )}
                </div>
              </div>
            ))}
            <div className="flex justify-end">
              <button
                type="button"
                onClick={onReset}
                className="inline-flex min-h-[44px] items-center justify-center rounded-lg border border-orange-200 bg-white px-4 py-2 text-sm font-semibold text-sylvan-700 hover:bg-orange-50"
              >
                <RefreshCw className="mr-2" size={15} />
                Check Different Documents
              </button>
            </div>
          </>
        )}
      </div>
    </section>
  );
};

const requiredManualRedlineEdits = (review) => (
  (review?.proposedEdits || []).filter((edit) => (
    edit.operation === 'manual_fill'
    || (['insert', 'replace'].includes(edit.operation) && !edit.clauseId)
  ))
);

// A blank administrative slot — an underscore/dash run with no real words. Its value
// is typed from scratch; only anchors that carry surrounding text are edited in place.
const anchorIsPureBlank = (edit) => /^[\s_\-–—.·]*$/.test(String(edit.anchor?.exactAnchorQuote || ''));

const initialRedlineValues = (review) => Object.fromEntries(
  requiredManualRedlineEdits(review).map((edit) => [
    edit.editId,
    edit.anchor?.placement === 'replace' && !anchorIsPureBlank(edit)
      ? edit.anchor?.exactAnchorQuote || ''
      : '',
  ])
);

const redlineValueIssue = (edit, value) => {
  const normalized = String(value || '').trim();
  if (!normalized) return `Enter the ${manualRedlineLabel(edit).toLowerCase()}.`;
  if (normalized.length > 2000) return 'Keep the value under 2,000 characters.';
  if (edit.anchor?.placement === 'replace'
    && normalized === String(edit.anchor?.exactAnchorQuote || '').trim()) {
    return 'Replace the blank with the verified value.';
  }
  if (/\[[A-Z][A-Z0-9 _/-]*\]/.test(normalized)) {
    return 'Replace all bracketed placeholders before generating.';
  }
  return null;
};

const redlineValueIsComplete = (edit, value) => redlineValueIssue(edit, value) === null;

const manualRedlineLabel = (edit) => {
  const value = `${edit.editId || ''} ${edit.rationale || ''}`.toLowerCase();
  if (value.includes('effective date') || value.includes('b06-date')) return 'Effective Date';
  if (value.includes('discloser') && value.includes('signature')) return 'Discloser Signature-Block Name';
  if (value.includes('recipient') && value.includes('signature')) return 'Recipient Signature-Block Name';
  if (value.includes('counterparty') && value.includes('name')) return 'Counterparty Legal Name';
  if (value.includes('counterparty') && (value.includes('mailing') || value.includes('address'))) return 'Counterparty Mailing Address';
  if (value.includes('discloser')) return 'Discloser Legal Information';
  if (value.includes('recipient')) return 'Recipient Legal Information';
  const field = /\bthe ([a-z][a-z0-9 /-]*?) field\b/.exec(value);
  if (field) {
    return field[1].split(' ').map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  }
  return 'Administrative Replacement';
};

const manualRedlinePlaceholder = (label) => {
  if (label === 'Counterparty Legal Name') return 'e.g. Acme Mechanical Contractors, Inc.';
  if (label === 'Counterparty Mailing Address') return 'Street, City, State/Province, ZIP';
  if (label === 'Effective Date') return 'e.g. August 26, 2026';
  return 'Enter the verified value';
};

// One input per distinct fact: pure-blank slots that resolved to the same specific
// label (e.g. the counterparty name in the preamble and the notices clause) share a
// single field whose value fills every location. In-place edits and fields with the
// generic fallback label stay separate — same label there does not mean same fact.
const groupedManualRedlineEdits = (review) => {
  const groups = [];
  const byLabel = new Map();
  requiredManualRedlineEdits(review).forEach((edit) => {
    const label = manualRedlineLabel(edit);
    const shareable = anchorIsPureBlank(edit) && label !== 'Administrative Replacement';
    if (shareable && byLabel.has(label)) {
      byLabel.get(label).edits.push(edit);
      return;
    }
    const group = { label, edits: [edit] };
    groups.push(group);
    if (shareable) byLabel.set(label, group);
  });
  return groups;
};

const manualRedlineLocationNote = (group) => {
  const pages = [...new Set(group.edits.map((edit) => edit.anchor?.pageNumber).filter(Boolean))];
  const pageText = pages.length ? ` (page${pages.length === 1 ? '' : 's'} ${pages.join(', ')})` : '';
  return group.edits.length > 1
    ? `One value fills ${group.edits.length} blank fields in the document${pageText}.`
    : `Fills the blank field in the document${pageText}.`;
};

const redlineStatusLabel = (status) => ({
  queued: 'Queued',
  running: 'Generating',
  succeeded: 'Ready',
  failed: 'Not Generated',
}[status] || 'Preparing');

const findRedlineArtifact = (artifacts) => (
  (artifacts || []).find((item) => /(?:nda_redline|nda_review_copy)\.(?:docx|pdf)$/i.test(item.name))
);

// Reviews cleared to sign need no marked-up copy; the other two dispositions get one.
export const ndaHasRedline = (review) => (
  ['edit_and_recirculate', 'escalate_to_legal'].includes(review?.disposition)
);

// What the menu's redline option will hand over, so the reviewer knows before clicking.
export const ndaRedlineMenuNote = (review, sourceName) => {
  if (!ndaHasRedline(review)) return 'Not needed: this NDA can be signed as-is.';
  const format = /\.pdf$/i.test(sourceName || '') ? 'PDF' : 'Word';
  return review.disposition === 'escalate_to_legal'
    ? `The NDA (${format}) with each finding noted in the margin, for Legal`
    : `The NDA (${format}) with the proposed edits marked up`;
};

const redlineDownloadName = (sourceName, artifactName) => {
  if (!sourceName) return artifactName;
  const extension = artifactName.toLowerCase().endsWith('.pdf') ? '.pdf' : '.docx';
  return `${sourceName.replace(/\.[^.]+$/, '')}_redlined${extension}`;
};

const userSafeRedlineFailureMessage = (message) => {
  const value = String(message || '');
  const normalized = value.toLowerCase();
  if (normalized.includes('predates native-redline') || normalized.includes('before native-redline')) {
    return 'This NDA was reviewed before native redlines were enabled. Submit it for a new review first.';
  }
  if (normalized.includes('placed safely') || normalized.includes('preflight')) {
    return 'The approved edits could not be placed safely in the original document. No partial redline was created.';
  }
  if (normalized.includes('did not pass document validation') || normalized.includes('validation')) {
    return 'The generated document did not pass validation. No redline was published.';
  }
  if (normalized.includes('phase 2a') || normalized.includes('does not include native nda redline')) return value;
  if (normalized.includes('{') || normalized.includes('traceback') || normalized.includes('request_id')) {
    return 'The native NDA redline could not be generated. Please try again.';
  }
  return value || 'The native NDA redline could not be generated. Please try again.';
};

const userSafeDraftFailureMessage = (message) => {
  const value = String(message || '');
  const normalized = value.toLowerCase();
  if (normalized.includes('phase 2b') || normalized.includes('does not include sylvan nda drafting')) return value;
  if (normalized.includes('template') && normalized.includes('unavailable')) {
    return 'The approved Sylvan NDA template is temporarily unavailable. Please contact an administrator.';
  }
  if (normalized.includes('validation') || normalized.includes('generated nda did not pass')) {
    return 'The generated NDA did not pass document validation. No draft was published.';
  }
  if (normalized.includes('{') || normalized.includes('traceback') || normalized.includes('request_id')) {
    return 'The Sylvan NDA draft could not be generated. Please try again.';
  }
  return value || 'The Sylvan NDA draft could not be generated. Please try again.';
};

const NdaHomeTabs = ({ active, onChange }) => (
  <div className="mx-auto grid w-full max-w-3xl grid-cols-1 gap-1 rounded-lg border border-gray-200 bg-white p-1 sm:grid-cols-3" role="tablist" aria-label="NDA workflows">
    {[
      { id: 'review', label: 'Review an NDA', icon: <ShieldCheck size={17} /> },
      { id: 'draft', label: 'Draft Sylvan NDA', icon: <Edit3 size={17} /> },
      { id: 'register', label: 'NDA Register', icon: <Archive size={17} /> },
    ].map((tab) => (
      <button
        key={tab.id}
        type="button"
        role="tab"
        aria-selected={active === tab.id}
        onClick={() => onChange(tab.id)}
        className={`inline-flex min-h-[44px] items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-semibold transition-colors ${
          active === tab.id
            ? 'bg-sylvan-500 text-white shadow-sm'
            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
        }`}
      >
        {tab.icon}
        {tab.label}
      </button>
    ))}
  </div>
);

const NdaDraftIntakePanel = ({ form, onChange, onGenerate }) => {
  const complete = form.counterpartyLegalName.trim()
    && form.counterpartyAddressLine1.trim()
    && form.counterpartyCity.trim()
    && form.counterpartyState.trim()
    && form.counterpartyPostalCode.trim()
    && form.businessPurpose.trim()
    && form.effectiveDate
    && form.sylvanEntity
    && Number.isInteger(Number(form.termYears))
    && Number(form.termYears) >= 1
    && Number(form.termYears) <= 10;
  const fieldClass = 'mt-2 w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 outline-none focus:border-sylvan-400 focus:ring-2 focus:ring-orange-100';
  return (
    <section className="min-w-0 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
      <div className="border-b border-gray-200 px-5 py-5 sm:px-6">
        <div className="flex items-start gap-3">
          <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-orange-50 text-sylvan-600">
            <Edit3 size={19} />
          </span>
          <div>
            <h2 className="text-lg font-bold text-gray-950">Draft Sylvan NDA</h2>
            <p className="mt-1 text-sm leading-6 text-gray-600">
              Create a mutual NDA from Sylvan’s approved form. Legal language and survival terms remain unchanged.
            </p>
          </div>
        </div>
      </div>
      <div className="grid gap-5 px-5 py-5 sm:grid-cols-2 sm:px-6">
        <label className="text-sm font-semibold text-gray-800">
          Sylvan Party <span className="text-red-600">*</span>
          <select
            value={form.sylvanEntity}
            onChange={(event) => {
              const entity = event.target.value;
              onChange('sylvanEntity', entity);
              // Legal's form pairs the Canadian entity with Ontario law and the rest with Michigan.
              onChange('governingLaw', entity === NDA_CANADA_ENTITY ? 'ontario' : 'michigan');
            }}
            className={fieldClass}
          >
            <option value="">Select the Sylvan party</option>
            {NDA_SYLVAN_ENTITIES.map((entity) => (
              <option key={entity} value={entity}>{entity}</option>
            ))}
          </select>
        </label>
        <label className="text-sm font-semibold text-gray-800">
          Governing Law <span className="text-red-600">*</span>
          <select
            value={form.governingLaw}
            onChange={(event) => onChange('governingLaw', event.target.value)}
            className={fieldClass}
          >
            {Object.entries(NDA_GOVERNING_LAWS).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </label>
        <label className="text-sm font-semibold text-gray-800">
          Counterparty Legal Name <span className="text-red-600">*</span>
          <input
            type="text"
            maxLength={200}
            value={form.counterpartyLegalName}
            onChange={(event) => onChange('counterpartyLegalName', event.target.value)}
            placeholder="Example Company, LLC"
            className={fieldClass}
          />
        </label>
        <label className="text-sm font-semibold text-gray-800">
          Effective Date <span className="text-red-600">*</span>
          <input
            type="date"
            value={form.effectiveDate}
            onChange={(event) => onChange('effectiveDate', event.target.value)}
            className={fieldClass}
          />
        </label>
        <label className="text-sm font-semibold text-gray-800 sm:col-span-2">
          Address Line 1 <span className="text-red-600">*</span>
          <input
            type="text"
            autoComplete="address-line1"
            maxLength={200}
            value={form.counterpartyAddressLine1}
            onChange={(event) => onChange('counterpartyAddressLine1', event.target.value)}
            placeholder="123 Main Street"
            className={fieldClass}
          />
        </label>
        <label className="text-sm font-semibold text-gray-800 sm:col-span-2">
          Address Line 2 <span className="font-normal text-gray-500">(optional)</span>
          <input
            type="text"
            autoComplete="address-line2"
            maxLength={200}
            value={form.counterpartyAddressLine2}
            onChange={(event) => onChange('counterpartyAddressLine2', event.target.value)}
            placeholder="Suite, unit, building, floor"
            className={fieldClass}
          />
        </label>
        <div className="grid gap-5 sm:col-span-2 sm:grid-cols-6">
          <label className="text-sm font-semibold text-gray-800 sm:col-span-2">
            City <span className="text-red-600">*</span>
            <input
              type="text"
              autoComplete="address-level2"
              maxLength={100}
              value={form.counterpartyCity}
              onChange={(event) => onChange('counterpartyCity', event.target.value)}
              placeholder="Detroit"
              className={fieldClass}
            />
          </label>
          <label className="min-w-0 text-sm font-semibold text-gray-800 sm:col-span-2">
            State <span className="text-red-600">*</span>
            <select
              autoComplete="address-level1"
              value={form.counterpartyState}
              onChange={(event) => onChange('counterpartyState', event.target.value)}
              className={fieldClass}
            >
              <option value="">Select State</option>
              {US_STATES.map(([abbreviation, name]) => (
                <option key={abbreviation} value={abbreviation}>{name} ({abbreviation})</option>
              ))}
            </select>
          </label>
          <label className="text-sm font-semibold text-gray-800 sm:col-span-2">
            ZIP Code <span className="text-red-600">*</span>
            <input
              type="text"
              inputMode="numeric"
              autoComplete="postal-code"
              maxLength={20}
              value={form.counterpartyPostalCode}
              onChange={(event) => onChange('counterpartyPostalCode', event.target.value)}
              placeholder="48201"
              className={fieldClass}
            />
          </label>
        </div>
        <label className="text-sm font-semibold text-gray-800 sm:col-span-2">
          Business Purpose <span className="text-red-600">*</span>
          <input
            type="text"
            maxLength={500}
            value={form.businessPurpose}
            onChange={(event) => onChange('businessPurpose', event.target.value)}
            placeholder="Evaluate a potential project"
            className={fieldClass}
          />
        </label>
        <label className="text-sm font-semibold text-gray-800">
          Agreement Term <span className="text-red-600">*</span>
          <div className="relative mt-2">
            <input
              type="number"
              min="1"
              max="10"
              step="1"
              value={form.termYears}
              onChange={(event) => onChange('termYears', event.target.value)}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 pr-16 text-sm text-gray-900 outline-none focus:border-sylvan-400 focus:ring-2 focus:ring-orange-100"
            />
            <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm text-gray-500">years</span>
          </div>
          <span className="mt-1 block text-xs font-normal text-gray-500">Choose a whole number from 1 to 10.</span>
        </label>
        <div>
          <p className="text-sm font-semibold text-gray-800">Information Flow</p>
          <div className="mt-2 flex min-h-[42px] items-center rounded-lg border border-gray-200 bg-gray-50 px-3 text-sm font-semibold text-gray-700">
            <ShieldCheck className="mr-2 text-sylvan-500" size={16} />
            Mutual
          </div>
          <p className="mt-1 text-xs text-gray-500">The approved drafting template supports mutual NDAs only.</p>
        </div>
      </div>
      <div className="flex justify-end border-t border-gray-200 px-5 py-4 sm:px-6">
        <button
          type="button"
          onClick={onGenerate}
          disabled={!complete}
          className="inline-flex min-h-[44px] w-full items-center justify-center rounded-lg bg-sylvan-500 px-5 py-2 text-sm font-semibold text-white hover:bg-sylvan-600 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
        >
          <Edit3 className="mr-2" size={16} />
          Generate Sylvan NDA
        </button>
      </div>
    </section>
  );
};

const NdaDraftProcessingCard = ({ job }) => (
  <section className="rounded-xl bg-white p-6 shadow-sm" aria-live="polite">
    <div className="flex items-start gap-3">
      <Loader2 className="mt-0.5 shrink-0 animate-spin text-sylvan-500" size={21} />
      <div>
        <h2 className="font-bold text-gray-950">Generating Sylvan NDA</h2>
        <p className="mt-1 text-sm text-gray-600">{job?.progressMessage || 'Preparing the approved Sylvan NDA.'}</p>
      </div>
    </div>
    <div className="mt-5 h-2 overflow-hidden rounded-full bg-orange-50">
      <div className="h-full rounded-full bg-sylvan-500 transition-all duration-500" style={{ width: `${ndaDraftProgress(job?.stage)}%` }} />
    </div>
  </section>
);

const NdaDraftSuccessPanel = ({ form, downloading, onDownload, onReset, resetLabel = 'Draft another NDA' }) => (
  <section className="overflow-hidden rounded-xl border border-emerald-200 bg-white shadow-sm">
    <div className="flex items-start gap-3 border-b border-emerald-100 bg-emerald-50 px-5 py-5 sm:px-6">
      <CheckCircle2 className="mt-0.5 shrink-0 text-emerald-700" size={22} />
      <div>
        <h2 className="font-bold text-emerald-950">Sylvan NDA draft is ready</h2>
        <p className="mt-1 text-sm leading-6 text-emerald-900">The approved form was populated and passed document validation.</p>
      </div>
    </div>
    <dl className="grid gap-x-6 gap-y-4 px-5 py-5 text-sm sm:grid-cols-2 sm:px-6">
      <DraftSummaryItem label="Sylvan Party" value={form.sylvanEntity} />
      <DraftSummaryItem label="Governing Law" value={NDA_GOVERNING_LAWS[form.governingLaw] || form.governingLaw} />
      <DraftSummaryItem label="Counterparty" value={form.counterpartyLegalName} />
      <DraftSummaryItem label="Effective Date" value={form.effectiveDate} />
      <DraftSummaryItem label="Business Purpose" value={form.businessPurpose} wide />
      <DraftSummaryItem label="Agreement Term" value={`${form.termYears} year${Number(form.termYears) === 1 ? '' : 's'}`} />
      <DraftSummaryItem label="Information Flow" value="Mutual" />
    </dl>
    <p className="mx-5 mb-5 text-xs leading-5 text-gray-500 sm:mx-6">
      This draft is not legal approval, signature authority, or authorization to bind Sylvan. Review it before circulation or signature.
    </p>
    <div className="flex flex-col-reverse gap-3 border-t border-gray-200 px-5 py-4 sm:flex-row sm:justify-end sm:px-6">
      <button type="button" onClick={onReset} className="inline-flex min-h-[44px] items-center justify-center rounded-lg border border-orange-200 bg-white px-4 py-2 text-sm font-semibold text-sylvan-700 hover:bg-orange-50">
        {resetLabel === 'Back to NDA Register' ? <ArrowLeft className="mr-2" size={16} /> : <RefreshCw className="mr-2" size={16} />}
        {resetLabel}
      </button>
      <button type="button" onClick={onDownload} disabled={downloading} className="inline-flex min-h-[44px] items-center justify-center rounded-lg bg-sylvan-500 px-4 py-2 text-sm font-semibold text-white hover:bg-sylvan-600 disabled:opacity-50">
        {downloading ? <Loader2 className="mr-2 animate-spin" size={16} /> : <Download className="mr-2" size={16} />}
        Download DOCX
      </button>
    </div>
  </section>
);

const DraftSummaryItem = ({ label, value, wide = false }) => (
  <div className={wide ? 'sm:col-span-2' : ''}>
    <dt className="font-semibold text-gray-500">{label}</dt>
    <dd className="mt-1 break-words font-medium text-gray-900">{value}</dd>
  </div>
);

const NdaRegisterPanel = ({
  entries,
  pagination,
  filters,
  loading,
  error,
  onFiltersChange,
  onSearch,
  onClear,
  onPageChange,
  onOpen,
}) => {
  const update = (field, value) => onFiltersChange((current) => ({ ...current, [field]: value }));
  return (
    <section className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
      <div className="border-b border-gray-200 px-4 py-5 sm:px-6">
        <div className="flex items-start gap-3">
          <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-orange-50 text-sylvan-600">
            <Archive size={19} />
          </span>
          <div>
            <h2 className="text-lg font-bold text-gray-950">NDA Register</h2>
            <p className="mt-1 text-sm leading-6 text-gray-600">Search completed NDA reviews and generated Sylvan drafts.</p>
          </div>
        </div>
        <form className="mt-5 grid min-w-0 gap-3 xl:grid-cols-[minmax(220px,1fr)_180px_220px_auto]" onSubmit={(event) => { event.preventDefault(); onSearch(); }}>
          <label className="relative min-w-0">
            <span className="sr-only">Search NDA records</span>
            <Search className="pointer-events-none absolute left-3 top-3 text-gray-400" size={17} />
            <input
              value={filters.query}
              onChange={(event) => update('query', event.target.value)}
              placeholder="Counterparty, purpose, or filename"
              maxLength={300}
              className="h-11 w-full rounded-lg border border-gray-300 bg-white pl-10 pr-3 text-sm outline-none focus:border-sylvan-400 focus:ring-2 focus:ring-orange-100"
            />
          </label>
          <select value={filters.entryType} onChange={(event) => update('entryType', event.target.value)} className="h-11 min-w-0 rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-700 outline-none focus:border-sylvan-400 focus:ring-2 focus:ring-orange-100" aria-label="Record type">
            <option value="">All record types</option>
            <option value="review">Reviews</option>
            <option value="draft">Sylvan drafts</option>
          </select>
          <select value={filters.disposition} onChange={(event) => update('disposition', event.target.value)} className="h-11 min-w-0 rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-700 outline-none focus:border-sylvan-400 focus:ring-2 focus:ring-orange-100" aria-label="Disposition">
            <option value="">All Information Flows</option>
            <option value="eligible_to_sign_under_guide">Eligible under guide</option>
            <option value="edit_and_recirculate">Edit and recirculate</option>
            <option value="escalate_to_legal">Escalate to Legal</option>
          </select>
          <div className="flex min-w-0 gap-2">
            <button type="submit" className="inline-flex h-11 min-w-0 flex-1 items-center justify-center rounded-lg bg-sylvan-500 px-4 text-sm font-semibold text-white hover:bg-sylvan-600 xl:flex-none">Search</button>
            <button type="button" onClick={onClear} className="inline-flex h-11 min-w-0 flex-1 items-center justify-center rounded-lg border border-gray-300 bg-white px-3 text-sm font-semibold text-gray-700 hover:bg-gray-50 xl:flex-none">Clear</button>
          </div>
        </form>
      </div>

      {error ? (
        <div className="m-4 flex items-start gap-3 rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-900 sm:m-6" role="alert">
          <AlertTriangle className="mt-0.5 shrink-0" size={18} />
          <div><p className="font-bold">NDA Register unavailable</p><p className="mt-1">{error}</p></div>
        </div>
      ) : loading ? (
        <div className="flex min-h-[220px] items-center justify-center gap-3 text-sm font-medium text-gray-600" aria-live="polite">
          <Loader2 className="animate-spin text-sylvan-500" size={20} /> Loading NDA records
        </div>
      ) : entries.length === 0 ? (
        <div className="flex min-h-[220px] flex-col items-center justify-center px-6 text-center">
          <Archive className="text-gray-300" size={34} />
          <p className="mt-3 font-semibold text-gray-800">No completed NDA records found</p>
          <p className="mt-1 text-sm text-gray-500">Try clearing the filters or complete an NDA review or draft.</p>
        </div>
      ) : (
        <>
          <div className="hidden overflow-x-auto xl:block">
            <table className="w-full table-fixed text-left text-sm">
              <thead className="border-b border-gray-200 bg-gray-50 text-xs font-semibold uppercase text-gray-500">
                <tr>
                  <th className="w-[29%] px-5 py-3">Counterparty</th>
                  <th className="w-[12%] px-3 py-3">Type</th>
                  <th className="w-[20%] px-3 py-3">Result</th>
                  <th className="w-[14%] px-3 py-3">Information Flow</th>
                  <th className="w-[15%] px-3 py-3">Completed</th>
                  <th className="w-[10%] px-3 py-3"><span className="sr-only">Open</span></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {entries.map((entry) => (
                  <tr key={entry.entryId} className="hover:bg-orange-50/30">
                    <td className="px-5 py-4"><p className="truncate font-semibold text-gray-900">{entry.counterparty}</p><p className="mt-1 truncate text-xs text-gray-500">{entry.sourceFilename || entry.businessPurpose || 'Generated Sylvan NDA'}</p></td>
                    <td className="px-3 py-4 font-medium text-gray-700">{entry.entryType === 'review' ? 'Review' : 'Draft'}</td>
                    <td className="px-3 py-4"><NdaRegisterResult entry={entry} /></td>
                    <td className="px-3 py-4 text-gray-600">{formatInformationFlow(entry.informationFlow)}</td>
                    <td className="px-3 py-4 text-gray-600">{formatRegisterDate(entry.sourceCompletedAt)}</td>
                    <td className="px-3 py-4 text-right"><button type="button" onClick={() => onOpen(entry)} className="rounded-md px-3 py-2 font-semibold text-sylvan-700 hover:bg-orange-50">Open</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="space-y-4 p-4 xl:hidden">
            {entries.map((entry) => (
              <article key={entry.entryId} className="rounded-lg border border-gray-200 bg-white p-4">
                <div className="flex flex-col items-start gap-3 sm:flex-row sm:justify-between"><div className="min-w-0"><h3 className="break-words font-bold text-gray-900">{entry.counterparty}</h3><p className="mt-1 break-words text-xs text-gray-500">{entry.sourceFilename || entry.businessPurpose || 'Generated Sylvan NDA'}</p></div><NdaRegisterResult entry={entry} /></div>
                <dl className="mt-4 grid grid-cols-2 gap-3 text-xs"><div><dt className="font-semibold text-gray-500">Type</dt><dd className="mt-1 text-gray-800">{entry.entryType === 'review' ? 'Review' : 'Draft'}</dd></div><div><dt className="font-semibold text-gray-500">Completed</dt><dd className="mt-1 text-gray-800">{formatRegisterDate(entry.sourceCompletedAt)}</dd></div><div><dt className="font-semibold text-gray-500">Information Flow</dt><dd className="mt-1 text-gray-800">{formatInformationFlow(entry.informationFlow)}</dd></div></dl>
                <button type="button" onClick={() => onOpen(entry)} className="mt-4 inline-flex min-h-[44px] w-full items-center justify-center rounded-lg bg-sylvan-500 px-4 text-sm font-semibold text-white hover:bg-sylvan-600">Open record</button>
              </article>
            ))}
          </div>
        </>
      )}

      {!loading && !error && pagination.total > 0 && (
        <div className="flex flex-col gap-3 border-t border-gray-200 px-4 py-4 text-sm sm:flex-row sm:items-center sm:justify-between sm:px-6">
          <p className="text-gray-500">{pagination.total} completed record{pagination.total === 1 ? '' : 's'}</p>
          <div className="flex items-center justify-between gap-3 sm:justify-end">
            <button type="button" onClick={() => onPageChange(pagination.page - 1)} disabled={!pagination.hasPreviousPage} className="inline-flex min-h-[40px] items-center rounded-lg border border-gray-300 bg-white px-3 font-semibold text-gray-700 disabled:opacity-40"><ChevronLeft className="mr-1" size={16} /> Previous</button>
            <span className="tabular-nums text-gray-600">Page {pagination.page} of {Math.max(pagination.totalPages, 1)}</span>
            <button type="button" onClick={() => onPageChange(pagination.page + 1)} disabled={!pagination.hasNextPage} className="inline-flex min-h-[40px] items-center rounded-lg border border-gray-300 bg-white px-3 font-semibold text-gray-700 disabled:opacity-40">Next <ChevronRight className="ml-1" size={16} /></button>
          </div>
        </div>
      )}
    </section>
  );
};

const NdaRegisterResult = ({ entry }) => {
  if (entry.entryType === 'draft') return <span className="inline-flex rounded-full bg-gray-100 px-2 py-1 text-xs font-semibold text-gray-700">Draft created</span>;
  const result = ndaDispositionDisplay(entry.disposition);
  const style = entry.disposition === 'escalate_to_legal'
    ? 'bg-rose-50 text-rose-800'
    : entry.disposition === 'edit_and_recirculate'
      ? 'bg-amber-50 text-amber-800'
      : 'bg-emerald-50 text-emerald-800';
  return <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${style}`}>{result.label}</span>;
};

const NdaHistoricalRecordDetails = ({ detail, downloading, onDownload }) => {
  const redlineArtifacts = (detail.redlines || []).flatMap((redline) => (
    (redline.artifacts || []).filter((item) => /(?:nda_redline|nda_review_copy)\.(?:docx|pdf)$/i.test(item.name))
  ));
  const downloads = [
    ...(detail.originalAvailable ? [{ name: detail.sourceFilename || 'Original NDA', label: 'Original NDA', url: detail.originalUrl }] : []),
    ...redlineArtifacts.map((item) => ({ ...item, label: 'Validated Redline' })),
  ];
  return (
    <section className="mb-5 rounded-lg border border-gray-200 bg-white px-4 py-4 shadow-sm sm:px-5">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
        <div className="min-w-0">
          <p className="text-xs font-bold uppercase text-gray-500">Historical NDA Record</p>
          <h2 className="mt-1 break-words text-base font-bold text-gray-950">{detail.counterparty}</h2>
          <div className="mt-1 flex flex-wrap items-center gap-x-5 gap-y-1 text-xs text-gray-600">
            <p><span className="font-semibold text-gray-500">Information Flow:</span> {formatInformationFlow(detail.informationFlow)}</p>
            <p><span className="font-semibold text-gray-500">Completed:</span> {formatRegisterDate(detail.sourceCompletedAt)}</p>
          </div>
        </div>
        {downloads.length > 0 && (
          <div className="flex shrink-0 flex-wrap gap-2">
            {downloads.map((item) => (
              <button key={`${item.url}-${item.name}`} type="button" onClick={() => onDownload(item.url, item.name)} disabled={Boolean(downloading)} className="inline-flex min-h-[40px] items-center rounded-lg border border-orange-200 bg-white px-3 text-sm font-semibold text-sylvan-700 hover:bg-orange-50 disabled:opacity-50">
                {downloading === item.name ? <Loader2 className="mr-2 animate-spin" size={15} /> : <Download className="mr-2" size={15} />}{item.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

const formatRegisterDate = (value) => value ? new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' }).format(new Date(value)) : 'Not available';
const formatInformationFlow = (value) => ({ mutual: 'Mutual', sylvan_discloses: 'Sylvan discloses', counterparty_discloses: 'Counterparty discloses' }[value] || 'Not available');

const ndaDraftProgress = (stage) => ({
  queued: 0,
  template_loading: 15,
  purpose_formatting: 30,
  document_generation: 45,
  document_validation: 72,
  artifact_persistence: 92,
  completed: 100,
}[stage] ?? 0);

const NdaIntakePanel = ({
  file,
  informationFlow,
  supportingFiles,
  onSupportingFilesChange,
  onFileChange,
  onInformationFlowChange,
  onStart,
}) => (
  <section className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
    <div className="border-b border-gray-200 px-5 py-5 sm:px-6">
      <div className="flex items-start gap-3">
        <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-orange-50 text-sylvan-600">
          <ShieldCheck size={19} />
        </span>
        <div>
          <h2 className="text-lg font-bold text-gray-950">Review an NDA</h2>
          <p className="mt-1 text-sm leading-6 text-gray-600">Upload one PDF or DOCX and select the expected information flow.</p>
        </div>
      </div>
    </div>
    <div className="space-y-5 px-5 py-5 sm:px-6">
      <NdaFilePicker file={file} onChange={onFileChange} />
      <fieldset>
        <legend className="mb-2 text-sm font-semibold text-gray-800">Expected Information Flow <span className="text-red-600">*</span></legend>
        <div className="grid gap-2 sm:grid-cols-3">
          {NDA_INFORMATION_FLOW_OPTIONS.map((option) => (
            <label
              key={option.value}
              className={`cursor-pointer rounded-lg border px-3 py-3 text-sm ${
                informationFlow === option.value
                  ? 'border-sylvan-400 bg-orange-50 text-sylvan-800'
                  : 'border-gray-200 bg-white text-gray-700 hover:border-sylvan-200'
              }`}
            >
              <input
                type="radio"
                name="nda-agent-information-flow"
                value={option.value}
                checked={informationFlow === option.value}
                onChange={(event) => onInformationFlowChange(event.target.value)}
                className="mr-2 accent-orange-600"
              />
              <span className="font-semibold">{option.shortLabel}</span>
              <span className="mt-1 block text-xs leading-5 text-gray-500">{option.description}</span>
            </label>
          ))}
        </div>
      </fieldset>
      <div>
        <label htmlFor="nda-intake-supporting-files" className="text-sm font-semibold text-gray-800">
          Supporting documents <span className="font-normal text-gray-500">(optional — up to 5 PDF or DOCX files)</span>
        </label>
        <p className="mt-1 text-xs leading-5 text-gray-500">
          Proposals, bid documents, or contract exhibits. They are checked against the NDA for
          contradictions once the review finishes. Findings are advisory and never change the review result.
        </p>
        <input
          id="nda-intake-supporting-files"
          type="file"
          multiple
          accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          onChange={(event) => {
            const selected = Array.from(event.target.files || []);
            onSupportingFilesChange([...supportingFiles, ...selected].slice(0, 5));
            event.target.value = '';
          }}
          className="mt-2 block w-full text-sm text-gray-700 file:mr-3 file:rounded-full file:border-0 file:bg-orange-50 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-sylvan-700 hover:file:bg-orange-100"
        />
        {supportingFiles.length > 0 && (
          <ul className="mt-3 space-y-2">
            {supportingFiles.map((item, index) => (
              <li key={`${item.name}-${index}`} className="flex items-center justify-between rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-800">
                <span className="min-w-0 truncate">{item.name}</span>
                <button
                  type="button"
                  onClick={() => onSupportingFilesChange(supportingFiles.filter((_, itemIndex) => itemIndex !== index))}
                  className="ml-3 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100"
                  aria-label={`Remove ${item.name}`}
                >
                  <X size={14} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
    <div className="flex justify-end border-t border-gray-200 px-5 py-4 sm:px-6">
      <button
        type="button"
        onClick={onStart}
        disabled={!file || !informationFlow}
        className="inline-flex min-h-[44px] w-full items-center justify-center rounded-lg bg-sylvan-500 px-5 py-2 text-sm font-semibold text-white hover:bg-sylvan-600 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
      >
        <ShieldCheck className="mr-2" size={16} />
        Start NDA review
      </button>
    </div>
  </section>
);

// Dropped files skip the input's accept filter, so the type is checked here too.
export const isNdaUploadFile = (file) => Boolean(file) && (
  /\.(pdf|docx)$/i.test(file.name || '')
  || ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'].includes(file.type)
);

const NdaFilePicker = ({ file, onChange }) => {
  const inputId = useId();
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [dropError, setDropError] = useState('');
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => inputRef.current?.click()}
      onDragOver={(event) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = 'copy';
        if (!dragging) setDragging(true);
      }}
      onDragLeave={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setDragging(false);
      }}
      onDrop={(event) => {
        event.preventDefault();
        setDragging(false);
        const dropped = event.dataTransfer.files?.[0];
        if (!dropped) return;
        if (!isNdaUploadFile(dropped)) {
          setDropError('Only PDF or DOCX files can be reviewed.');
          return;
        }
        setDropError('');
        onChange(dropped);
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          inputRef.current?.click();
        }
      }}
      className={`rounded-lg border-2 border-dashed px-4 py-6 text-center hover:border-sylvan-300 hover:bg-orange-50/40 ${
        dragging ? 'border-sylvan-400 bg-orange-50' : 'border-gray-300 bg-gray-50'
      }`}
    >
      <input
        ref={inputRef}
        id={inputId}
        type="file"
        accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        className="hidden"
        onChange={(event) => {
          setDropError('');
          onChange(event.target.files?.[0] || null);
          event.target.value = '';
        }}
      />
      {file ? (
        <div className="flex items-center justify-center gap-3">
          <FileText className="shrink-0 text-sylvan-500" size={24} />
          <div className="min-w-0 text-left">
            <p className="truncate text-sm font-semibold text-gray-900">{file.name}</p>
            <p className="text-xs text-gray-500">Click to replace</p>
          </div>
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onChange(null);
            }}
            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-gray-500 hover:bg-white hover:text-gray-800"
            aria-label="Remove NDA"
          >
            <X size={17} />
          </button>
        </div>
      ) : (
        <>
          <UploadCloud className="mx-auto text-sylvan-500" size={28} />
          <p className="mt-2 text-sm font-semibold text-gray-900">
            {dragging ? 'Drop the NDA here' : 'Drag an NDA here, or click to upload'}
          </p>
          <p className="mt-1 text-xs text-gray-500">PDF or DOCX</p>
        </>
      )}
      {dropError && <p className="mt-2 text-xs font-semibold text-rose-700" role="alert">{dropError}</p>}
    </div>
  );
};

const NdaProcessingCard = ({ job }) => {
  const progress = ndaProgress(job);
  const [remainingSeconds, setRemainingSeconds] = useState(null);

  useEffect(() => {
    const estimate = Number(job?.estimatedRemainingSeconds);
    setRemainingSeconds(Number.isFinite(estimate) && estimate >= 0 ? Math.ceil(estimate) : null);
  }, [job?.estimatedRemainingSeconds, job?.estimateUpdatedAt, job?.stage]);

  const hasEstimate = remainingSeconds !== null;
  useEffect(() => {
    if (!hasEstimate || job?.status === 'failed' || job?.status === 'succeeded') {
      return undefined;
    }
    const timer = window.setInterval(() => {
      setRemainingSeconds((current) => (
        current === null ? null : Math.max(current - 1, 0)
      ));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [job?.status, hasEstimate]);

  const estimateMessage = remainingSeconds === null
    ? null
    : remainingSeconds > 0
      ? `Estimated time remaining: ${formatNdaEta(remainingSeconds)}`
      : 'Taking longer than usual...';

  return (
    <section className="rounded-xl bg-white p-6 shadow-sm">
      <div className="flex items-start gap-3">
        <Loader2 className="mt-0.5 shrink-0 animate-spin text-sylvan-500" size={21} />
        <div className="min-w-0 flex-1">
          <h2 className="font-bold text-gray-950">Reviewing NDA with AI</h2>
          <p className="mt-1 text-sm text-gray-600">{job?.progressMessage || formatNdaJobStatus(job)}</p>
        </div>
      </div>
      <div className="mt-5 h-2 overflow-hidden rounded-full bg-gray-100">
        <div className="h-full rounded-full bg-sylvan-500 transition-all duration-500" style={{ width: `${progress}%` }} />
      </div>
      <div className="mt-2 flex justify-between text-xs font-medium text-gray-500">
        <span>{formatNdaJobStatus(job)}</span>
        <div className="text-right">
          <span>{progress}%</span>
          {estimateMessage && (
            <p className="mt-1 whitespace-nowrap text-sm font-semibold tabular-nums text-gray-700" aria-live="polite">
              {estimateMessage}
            </p>
          )}
        </div>
      </div>
    </section>
  );
};

const formatNdaEta = (seconds) => {
  const minutes = Math.floor(seconds / 60);
  const remaining = seconds % 60;
  return `${minutes}:${String(remaining).padStart(2, '0')}`;
};

const MobilePaneButton = ({ active, icon, label, onClick }) => (
  <button
    type="button"
    role="tab"
    aria-selected={active}
    onClick={onClick}
    className={`inline-flex min-h-[44px] items-center justify-center gap-2 rounded-md px-3 text-sm font-semibold ${
      active ? 'bg-orange-50 text-sylvan-700' : 'text-gray-500'
    }`}
  >
    {icon}
    {label}
  </button>
);

const NdaAssistant = ({
  expanded,
  visible,
  onToggleExpanded,
  messages,
  input,
  loading,
  ruleCatalog,
  onInputChange,
  onKeyDown,
  onSubmit,
  onPromptClick,
  messagesEndRef,
}) => {
  const [showSuggestions, setShowSuggestions] = useState(true);
  return (
    <aside className={`${visible ? 'flex' : 'hidden'} min-h-0 min-w-0 w-full flex-col border-gray-200 bg-white min-[1400px]:flex ${expanded ? '' : 'min-[1400px]:border-l'}`}>
      <div className="shrink-0 border-b border-gray-200 px-3 py-3 sm:px-5 sm:py-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            <button
              type="button"
              onClick={onToggleExpanded}
              className="hidden h-8 w-8 shrink-0 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-800 min-[1400px]:inline-flex"
              title={expanded ? 'Collapse AI assistant' : 'Expand AI assistant'}
              aria-label={expanded ? 'Collapse AI assistant' : 'Expand AI assistant'}
            >
              {expanded ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
            </button>
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-orange-50 text-sylvan-600">
              <Bot size={19} />
            </span>
            <div className="min-w-0">
              <h2 className="text-base font-bold text-gray-950">AI Assistant</h2>
              <p className="truncate text-sm text-gray-500">Ask about this NDA review</p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setShowSuggestions((current) => !current)}
            className={`inline-flex h-8 w-8 items-center justify-center rounded-lg ${showSuggestions ? 'bg-orange-50 text-sylvan-700' : 'text-gray-500 hover:bg-gray-100'}`}
            title={showSuggestions ? 'Hide suggested questions' : 'Show suggested questions'}
            aria-label={showSuggestions ? 'Hide suggested questions' : 'Show suggested questions'}
          >
            <Lightbulb size={16} />
          </button>
        </div>
      </div>
      <div className={`min-h-0 flex-1 space-y-4 overflow-x-hidden overflow-y-auto px-3 py-4 sm:px-5 ${expanded ? 'mx-auto w-full max-w-5xl' : ''}`}>
        {messages.map((message) => (
          <NdaChatMessage key={message.id} message={message} ruleCatalog={ruleCatalog} />
        ))}
        {loading && (
          <div className="flex items-center text-sm text-gray-600">
            <Loader2 className="mr-2 animate-spin text-sylvan-500" size={16} />
            Reviewing your question...
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>
      <div className="shrink-0 border-t border-gray-200 bg-white px-3 py-3 sm:px-5">
        <div className={expanded ? 'mx-auto w-full max-w-5xl' : ''}>
          {showSuggestions && (
            <div className="mb-3 flex flex-wrap gap-2">
              {NDA_FOLLOW_UP_PROMPTS.map((prompt) => (
                <button
                  key={prompt}
                  type="button"
                  onClick={() => onPromptClick(prompt)}
                  disabled={loading}
                  className="rounded-full border border-gray-200 bg-white px-3 py-1.5 text-left text-xs font-medium text-gray-600 hover:border-orange-200 hover:bg-orange-50 hover:text-sylvan-700 disabled:opacity-50"
                >
                  {prompt}
                </button>
              ))}
            </div>
          )}
          <div className="flex items-end gap-2 rounded-xl border border-gray-300 bg-white p-2 focus-within:border-sylvan-400 focus-within:ring-2 focus-within:ring-orange-100">
            <textarea
              value={input}
              rows={expanded ? 3 : 2}
              onChange={(event) => onInputChange(event.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Ask about the NDA findings..."
              className="min-h-[44px] flex-1 resize-none border-0 px-2 py-2 text-sm text-gray-900 outline-none placeholder:text-gray-400"
            />
            <button
              type="button"
              onClick={onSubmit}
              disabled={loading || !input.trim()}
              className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-sylvan-500 text-white hover:bg-sylvan-600 disabled:cursor-not-allowed disabled:opacity-40"
              aria-label="Send question"
            >
              <Send size={17} />
            </button>
          </div>
        </div>
      </div>
    </aside>
  );
};

const NdaChatMessage = ({ message, ruleCatalog }) => {
  const isUser = message.role === 'user';
  return (
    <div className={`flex w-full min-w-0 ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div className="flex w-full min-w-0 flex-col items-start sm:max-w-[92%]">
        <div className="mb-1 flex items-center gap-2">
          <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-sylvan-100 text-sylvan-700">
            {isUser ? <User size={15} /> : <Bot size={15} />}
          </span>
          <span className="text-xs font-medium text-gray-500">{isUser ? 'You' : 'NDA Agent'}</span>
        </div>
        <div className={`max-w-full overflow-hidden rounded-lg border p-4 ${
          isUser ? 'border-sylvan-200 bg-sylvan-50' : 'border-gray-200 bg-white'
        }`}>
          {message.kind === 'error' ? (
            <div className="flex text-red-700">
              <AlertTriangle className="mr-2 mt-0.5 shrink-0" size={16} />
              <p className="text-sm">{message.text}</p>
            </div>
          ) : isUser ? (
            <p className="break-words whitespace-pre-wrap text-sm leading-6 text-gray-700">{message.text}</p>
          ) : (
            <>
              <MarkdownChatContent className="min-w-0 [overflow-wrap:anywhere]">{message.text}</MarkdownChatContent>
              {message.citedRuleIds?.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {message.citedRuleIds.map((ruleId) => {
                    const rule = ruleCatalog.find((item) => item.id === ruleId);
                    return (
                      <span key={ruleId} className="inline-flex flex-col rounded-lg border border-orange-200 bg-orange-50 px-2.5 py-1.5 text-sylvan-700">
                        <span className="text-xs font-semibold">{rule?.title || 'NDA policy rule'}</span>
                        <span className="text-[10px] font-medium text-gray-500">{ruleId}</span>
                      </span>
                    );
                  })}
                </div>
              )}
              {message.routedToLegal && (
                <p className="mt-2 text-xs font-semibold text-rose-700">This request was routed to Legal guidance.</p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const buildNdaAssistantBlurb = (review, fileName) => {
  const disposition = ndaDispositionDisplay(review?.disposition).label;
  const edits = review?.proposedEdits?.length || 0;
  const triggers = review?.escalationTriggers?.length || 0;
  return `I've reviewed ${fileName || 'the uploaded NDA'} against Sylvan’s NDA policy. The disposition is ${disposition}. The review identified ${edits} required edit${edits === 1 ? '' : 's'} and ${triggers} Legal trigger${triggers === 1 ? '' : 's'}. What would you like to explore?`;
};

const delay = (milliseconds) => new Promise((resolve) => setTimeout(resolve, milliseconds));

export default NdaAgent;
